import React, { useState, useEffect, useRef, useCallback } from 'react';
import styles from 'components/layout_and_quote_builder/_style.module.scss';
import ManufacturingLineCard from 'components/layout_and_quote_builder/ManufacturingLineCard.jsx';
import LayoutNavSectionCombinationListList from 'components/layout_and_quote_builder/LayoutNavSectionCombinationListList.jsx';
import { setInCache } from 'components/layout_and_quote_builder/utils/SolutionScoreCache.js';
import {
  fetchJsonCached, hasCachedResponse, getCachedResponse, setCachedResponse,
} from 'components/frank/utils/ManufacturingLineResponseCache.js';
import { pollSectionBestScores } from 'components/frank/utils/sectionBestScorePolling.js';
import { ProgressProvider, useProgress } from 'components/layout_and_quote_builder/utils/ProgressContext.jsx';
import { DebugPanel } from 'components/layout_and_quote_builder/DebugPopup.jsx';
// The listing's loading state is a modal, blocking popup -- a full-screen card with a short "Loading…" title and
// a determinate percentage bar while the manufacturing lines are scored. It reads more clearly than an inline
// bar. ListingProgressOverlay is Frank's own (it reuses the legacy overlay's styles but with the shorter title),
// so the legacy page's ProgressBarOverlay stays exactly as it is. The solve keeps Frank's own in-place skeleton
// (SolutionCardsSkeleton), whose single request has no percentage to show.
import ListingProgressOverlay from 'components/frank/ListingProgressOverlay.jsx';
import SolutionCardsSkeleton from 'components/frank/SolutionCardsSkeleton.jsx';
import SelectedSolutionSections from 'components/frank/SelectedSolutionSections.jsx';
// The shared de-select: it posts to the reset endpoint, clears the section server-side and tells the linked
// sections. A JS helper, not a Ruby service -- the same reuse the legacy cards get.
import resetSelectedListHandler from 'components/layout_and_quote_builder/quote_builder/resetSelectedListHandler.js';
import skeletonStyles from 'components/frank/_style.module.scss';
import sectionAnswerQueue from 'components/frank/utils/sectionAnswerQueue.js';
import {
  readSelectedManufacturingLineList,
  writeSelectedManufacturingLineList,
  clearSelectedManufacturingLineList,
  readSectionMachineTypes,
  readSectionDisabledFilters,
  readSectionAreaRatioPercent,
  writeSectionFilters,
} from 'components/frank/utils/manufacturingLineListUrlState.js';
import { combineSolutionGroups } from 'components/frank/utils/solutionGroups.js';
import { isLinkedSection, linkedParentOf } from 'components/frank/utils/linkedNavSection.js';

// The stable address of ONE distinct manufacturing line: (sub_section_index, identifier). The same line is
// reused across many lists, so its best score is fetched once under this key. See #bestScoreByLine.
const lineKey = (line) => `${line.sub_section_index}:${line.identifier}`;

// The star rating for a score: BEST (lowest) = 5 stars, worst = 1, by the score's position in the RANGE of the
// scores on screen -- the same shape the solution listing uses (sortSolutionsByScoreAndDrawStars). null for a
// plan with no score; 5 when there is only one score or they are all equal (all equally best).
const starRatingForScore = (score, minScore, maxScore) => {
  if (score == null) { return null; }

  const range = maxScore - minScore;
  if (!(range > 0)) { return 5; }

  const normalized = (score - minScore) / range;
  return Math.round(Math.max(1, Math.min(5, 5 - (normalized * 4))) * 10) / 10; // 5 down to 1, never 0
};

// A star row for a rating -- full stars, a half star, empty stars, then the numeric rating. The same rendering
// the legacy solution listing draws, in React.
const Stars = ({ rating }) => {
  if (rating == null) { return null; }

  const full = Math.floor(rating);
  const remainder = rating - full;
  const hasHalf = remainder >= 0.25 && remainder < 0.75;
  const empty = Math.max(0, 5 - full - (hasHalf ? 1 : 0));

  return (
    <span style={{ color: '#ffc107', fontSize: '14px' }}>
      {'★'.repeat(full)}
      {hasHalf ? (
        <span style={{ position: 'relative', display: 'inline-block' }}>
          <span style={{ color: '#ddd' }}>★</span>
          <span style={{ position: 'absolute', left: 0, width: '50%', overflow: 'hidden', color: '#ffc107' }}>★</span>
        </span>
      ) : null}
      {'☆'.repeat(empty)}
      <span style={{ marginLeft: '4px', color: '#666', fontSize: '13px' }}>{rating.toFixed(1)}</span>
    </span>
  );
};

// How many of each part's solutions, cheapest first, survive into the solutions grid's cartesian product. Read
// from ENV FRANK_TOP_N_PER_PART (via the page), deliberately NOT the legacy TOP_N_PER_PART, which the
// customer-facing quote builder listing reads. The server sends a POSITIVE integer or its own default
// (HomeController#frank_top_n_per_part), so 0 -- which would empty every multi-part plan -- never arrives here;
// the literal is only for surfaces that set no global at all, e.g. JS tests. See combineSolutionGroups.
const TOP_N_PER_PART = window.frank_top_n_per_part ?? 10;

// The area-ratio cap (a PERCENT: "20" == a printing bed up to 20% larger in area than the sheet is kept) that
// the /mcc Debug input starts on. Seeded from the server so it matches the applied default: the server reads
// the frank_machine_area_ratio_percent SystemParam (admin-tunable from the database, no deploy), which itself
// falls back to the filter's shipped default -- no second hardcoded value to drift. The literal fallback here
// is only for surfaces that do not set the global, e.g. JS tests.
const DEFAULT_AREA_RATIO_PERCENT = window.frank_machine_area_ratio_percent ?? 20;

// BEST FIRST, AND HOW MANY STARS -- copied from the legacy listing's own sortDivs
// (LayoutNavSectionCombinationList), so a solution ranks and reads identically on both pages.
//
// The stars are NOT a property of one solution: they are its score's position in the RANGE of the scores on
// screen (best = 5, worst = 1), so they can only be worked out once every card has answered. The legacy does it
// in the DOM rather than in React state for exactly that reason -- each card fetches its own score, and
// data-score appears on the grid item as each lands. Frank keeps the same shape, and takes only the two halves
// that are Frank's: the SORT and the STARS. The legacy's other two (the missing-machine button, the show-more
// limit) are its listing's own controls, which Frank does not have.
//
// LOWER IS BETTER: the score is a cost.
const sortSolutionsByScoreAndDrawStars = (grid) => {
  if (!grid) { return; }

  const items = Array.from(grid.children);
  if (items.length === 0) { return; }

  const scoreOf = (item) => parseInt(item.getAttribute('data-score'), 10);

  const sorted = [...items].sort((a, b) => scoreOf(a) - scoreOf(b));
  const alreadySorted = sorted.every((item, index) => item === items[index]);
  if (!alreadySorted) {
    sorted.forEach((item) => grid.appendChild(item));
  }

  // A score of 0 is "not answered yet", not "free" -- the legacy filters those out of the range, or an
  // unanswered card would clamp to a spurious 5.0 and sort to the front.
  const scores = items.map(scoreOf).filter((score) => !Number.isNaN(score) && score > 0);
  if (scores.length === 0) { return; }

  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  const range = maxScore - minScore;
  if (range <= 0) { return; }

  items.forEach((item) => {
    const score = scoreOf(item);
    if (Number.isNaN(score)) { return; }

    const normalizedScore = (score - minScore) / range;
    const starRating = 5 - (normalizedScore * 4); // 5 down to 1, never 0
    const rounded = Math.round(Math.max(1, Math.min(5, starRating)) * 10) / 10;

    let container = item.querySelector('.stars-rating-container');
    if (!container) {
      container = document.createElement('span');
      container.className = 'stars-rating-container';
      item.prepend(container);
    }

    const fullStars = Math.floor(rounded);
    const remainder = rounded - fullStars;
    const hasHalfStar = remainder >= 0.25 && remainder < 0.75;
    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

    let starsHtml = '<span style="color: #ffc107; font-size: 14px; margin-left: 8px;">';
    starsHtml += '★'.repeat(fullStars);
    if (hasHalfStar) {
      starsHtml += '<span style="position: relative; display: inline-block;">'
        + '<span style="color: #ddd;">★</span>'
        + '<span style="position: absolute; left: 0; width: 50%; overflow: hidden; color: #ffc107;">★</span>'
        + '</span>';
    }
    starsHtml += '☆'.repeat(Math.max(0, emptyStars));
    starsHtml += ` <span style="margin-left: 4px; color: #666; font-size: 13px;">${rounded.toFixed(1)}</span></span>`;

    container.innerHTML = starsHtml;
  });
};

// The label each filter's toggle shows, by the key the server sends. Reuses the legacy listing's own label
// keys, so a filter reads the same on both pages.
const FILTER_LABEL_KEYS = {
  smallest_die_cutting_machine_of_each_type: 'smallest_die_cutting_filter',
};

// Each machine-type filter's row heading, and how to label one of its ticks -- both the legacy listing's own
// keys, so the rows read identically on both pages.
const MACHINE_TYPE_FILTER_LABELS = {
  printing_machine_types: {
    headingKey: 'quote_builder.printing_machines',
    optionKey: (type) => `quote_builder.${type}`,
  },
  die_cutting_machine_types: {
    headingKey: 'quote_builder.die_cutting_machines',
    optionKey: (type) => `quote_builder.die_cutting_type_${type}`,
  },
};

// Frank lists a section's SubSectionManufacturingLines on /mcc, below the "Chosen material dimensions": per
// sub_section, the manufacturing lines that can make it, rendered through the SAME cards the legacy page uses
// (ManufacturingLineCard) -- so a manufacturing line reads identically on both pages.
//
// It lists SUB_SECTION manufacturing lines, not ManufacturingLineLists: a ManufacturingLineList is one
// complete way to manufacture the WHOLE section, which for overlapping sub_sections (Cover, Tray, and the
// combined "Tray & Cover" all describing the same parts) is an exact-cover across them. That comes later.
//
// What Frank does not have yet, and so does not render:
//   - "Material dimensions:" chips -- a Frank ManufacturingLine carries its members, not the material sheets
//     (the sheets are the finder's fit input). The card drops that block on its own when the key is absent.
//   - "Priced solutions" -- no Solution Finder yet, so the cards are told there is no count to wait for
//     (hidePricedSolutionCount) rather than being left dimmed behind a spinner forever.
//   - selection -- this is a read-only verify view while Frank grows, so the cards are not clickable.
//
// Admin-gated like the material-sheets line above it.
// The solution this section is already priced from, if any -- read back from the sections the page was
// rendered with (user_quote_nav_sections[].selected_lists), so a refresh or a shared link reopens on the
// priced section rather than on the solution list. It is stored per section, which is why it is looked up by
// sort_order rather than by position.
const restoreSelectedSolutions = (navSectionIndex) => {
  const sections = window.user_quote_nav_sections || [];
  const section = sections.find((candidate) => candidate.sort_order === navSectionIndex);
  const stored = section && section.selected_lists;

  return stored && stored.length > 0 ? stored : null;
};

// IS A SOLUTION CHOSEN? Asked of the LENGTH, never of truthiness, because the reset path sets the selection to
// an EMPTY ARRAY rather than to null -- `resetSelectedListHandler` does `setSelectedList(() => [])`, and the
// legacy section's own Back button (rendered inside QuoteBuilderNavSection, which is handed this setter) goes
// through it. `[]` is truthy in JavaScript, so a truthiness check kept the priced view on screen with nothing
// in it: the reset had already reached the server, and the page showed one orphaned button.
const hasSelection = (selectedSolutions) => Array.isArray(selectedSolutions) && selectedSolutions.length > 0;

// What a LINKED section's listing is: nothing, and nothing pending. Distinct from null, which the page reads as
// "still loading" and waits on forever for a section that never fetches.
const NO_LISTING_OF_ITS_OWN = { manufacturing_line_lists: [], machine_type_filters: [], filters: [] };

// THE BLINK the results give as a search lands: half strength, held, then straight back. No transition on
// either edge -- an eased fade over a large block of cards is easy to miss entirely, and what has to register
// is that something changed, not a graceful change.
const RESULTS_BLINK_MILLISECONDS = 250;
const RESULTS_BLINK_OPACITY = 0.5;

// The card flex-basis the wizard uses -- 3 across (33.333%) instead of the page's 4 (25%), because the modal
// is narrower than the full page and four line/solution cards (each two sub-cards wide) come out cramped.
const WIZARD_CARD_BASIS = 'calc(33.333% - 16px)';
const PAGE_CARD_BASIS = 'calc(25% - 16px)';

// How the scroll-to-the-selection below decides a card has finished growing: three frames at the same height.
// SETTLE_FRAME_LIMIT bounds the wait in frames rather than by a clock, so a card that never settles (an image
// that never loads) still lands instead of waiting for ever.
const SETTLED_FRAMES = 3;
const SETTLE_FRAME_LIMIT = 90;
// How long that watch will wait for the list to finish arriving and ordering itself before giving up on the
// glide -- about ten seconds, generous for a slow solve and short of waiting for ever.
const LIST_READY_FRAME_LIMIT = 600;
// The beat between the card settling and the page moving -- one full tick of the 200ms re-ordering pass with a
// little margin, so the glide goes to where the card ends up rather than where it was. Kept as short as that
// allows: a longer pause reads as the page hesitating.
const GLIDE_DELAY_MS = 250;

// What counts as the reader moving the page themselves, while that watch is waiting. Their INPUT, never a
// scroll position -- see #glideWhenSettled.
const READER_INPUT_EVENTS = ['wheel', 'touchmove', 'keydown'];
const READER_SCROLL_KEYS = ['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '];

// How many cards a full row holds: the wizard's modal is narrower and gives a card a third of the row, the page
// a quarter. The pair decides when a row is full, and so when there is spare width for the cards to be centred
// in -- change a width without its capacity and one or two cards start centring in a row they actually fill.
//
// Kept next to the bases above, but note the page's SOLUTION card width is NOT one of them: it comes from
// .combinations_grid_item_cont in the shared layout_and_quote_builder stylesheet (also 25%), because
// columnBasis is only passed in the wizard. A change there has to be reflected here too.
const WIZARD_CARDS_PER_ROW = 3;
const PAGE_CARDS_PER_ROW = 4;

// The scoring progress shown INLINE in the wizard's line stage while the manufacturing lines are still being
// scored -- so the wizard shows a determinate bar (not a page of blank-scored cards), and the cards appear only
// once every line has answered. On the normal page the full-screen ListingProgressOverlay does this instead;
// inside the wizard that overlay would sit behind the modal, so this reads the SAME useProgress() data and
// renders in-flow. Rendered inside ManufacturingLineLists' ProgressProvider, so useProgress() is available.
// onCancel stops the very pass this bar is reporting, so the button belongs UNDER the bar rather than up by the
// filters: what is being cancelled is what you are watching.
const WizardScoringProgress = ({ onCancel }) => {
  const { progress } = useProgress();
  // Until the first line's best score lands, `completed` is 0 -- a "0%" bar frozen for the whole (cold) first
  // best-score request reads as broken, so sweep indeterminately and hide the percentage until results land.
  const started = progress.total > 0 && progress.completed > 0;
  const percentage = started ? progress.percentage : 0;

  return (
    <div style={{ padding: '48px 20px', textAlign: 'center' }}>
      <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '14px' }}>
        {t('setup_wizard.scoring')}
      </div>
      <div style={{ maxWidth: '360px', margin: '0 auto', height: '8px', background: 'var(--surface-muted, #f1f3f5)', borderRadius: '4px', overflow: 'hidden', position: 'relative' }}>
        {started
          ? <div style={{ height: '100%', width: `${percentage}%`, background: 'var(--brand, #eb5424)', transition: 'width 200ms ease' }} />
          : <div className={skeletonStyles.indeterminateFill} />}
      </div>
      {started && <div style={{ fontSize: '12px', color: 'var(--ink-faint, #8a94a3)', marginTop: '8px' }}>{percentage}%</div>}
      {onCancel && (
        <div style={{ marginTop: '16px' }}>
          <button
            type="button"
            data-cancel-search
            onClick={onCancel}
            className="dct-button dct-button--secondary dct-button--small"
          >
            {t('quote_builder.cancel')}
          </button>
        </div>
      )}
    </div>
  );
};

// The uuids of a solution group, joined -- the stable identity of "this card", used to tell whether two
// groups are the same solution (e.g. a restored selection vs a freshly fetched grid). score_data is dropped
// on persistence, but the uuids survive, so this matches across a remount.
const groupUuids = (group) => (group || []).map((solution) => solution.diecutting_result.uuid).join('|');

// Append the quantity (count) to a fetch's query. The client sends count on all three /mcc fetches so the
// server scores / solves against the count the page is SHOWING -- not whatever user_quote.count happens to be,
// which the count field persists ASYNCHRONOUSLY (a server that read the quote could score a not-yet-saved
// quantity). It also puts count in the URL, so it is part of the response-cache key. Absent count (the legacy
// stacked view, which never passes it) is simply not appended, and the server falls back to the quote's count.
const setCountParam = (query, count) => {
  if (count !== undefined && count !== null) { query.set('count', count); }
};

// stageView: when set, this component renders ONLY ONE sub-view -- 'line' (the filter panel + the
// manufacturing-line listing), 'solution' (the picked plan's solution cards) or 'pricing' (the priced view). It
// is set by BOTH the guided setup wizard (FrankSetupWizard, which splits the flow into modal stages) and the
// section's three page tabs (FrankQuoteBuilderPage: "<Section> - Manufacturing Line / Solution / Pricing"), so
// both surfaces reuse this one component's fetching, scoring, solving and pricing. Absent (the legacy stacked
// admin view) leaves every behaviour unchanged.
//   inWizard distinguishes the two: in the wizard a solution click just MARKS the card (the wizard commits on
//   its own "Done") and cards are drawn 3-across for the narrow modal; on the page tabs a solution is marked
//   and then committed by the Pricing tab, and cards keep the page's 4-across layout.
//   onWizardPickedChange / onWizardSelectionChange report the marked plan / solution up to the wizard (for its
//   Next / Done). onLinePicked / onSolutionPicked fire on a USER click (not on restore) so the page tabs can
//   auto-advance to the next tab.
//   onGoToNextStage advances the page's section tabs one stage on (Manufacturing Line -> Solution -> Pricing).
//   The parent binds it to the stage AFTER the one on screen, so this component just calls it. It is the page's
//   explicit "Next" button under a SELECTED card; the wizard has its own Next in the modal footer, so it does
//   NOT pass this (its absence is what keeps the "Next" button off the wizard's cards).
//   onGoToManufacturingLine jumps the page's section tabs back to the Manufacturing Line stage. It backs the
//   "select a Manufacturing Line" prompt the Solution tab shows when the user arrives with nothing picked. The
//   wizard does not pass it (its own Next is disabled until a line is picked, so it never reaches that state).
const ManufacturingLineLists = ({
  navSectionIndex, bobbin, config, stageView, inWizard, count,
  onWizardPickedChange, onWizardSelectionChange, onLinePicked, onSolutionPicked,
  onGoToNextStage, onGoToManufacturingLine
}) => {
  // The RAW bobbin value (undefined until touched -> the server's part_type default) that the listing is found
  // over -- read off the one `bobbin` descriptor rather than as a second prop the parent has to keep in step
  // with bobbin.checked. Absent (no bobbin row on this page) reads as undefined, exactly as before.
  const bobbinEnabled = bobbin ? bobbin.enabled : undefined;

  // A LINKED SECTION HAS NOTHING TO CHOOSE. Its manufacturing line and its solution come from another section --
  // a corrugated section is produced together with the carton_lamination one that leads it, on one line, in one
  // pass -- so it lists nothing, solves nothing and scores nothing, and says so instead (renderLinkedNote).
  // ONLY those two are shared: it still prices itself, and its priced view renders like any other section's.
  //
  // "Nothing to choose" is not "nothing to ask". It still fetches the inherited PLAN, so its Manufacturing Line
  // tab has a card to draw -- against the LEADING section's index, since it has no manufacturing lines of its
  // own (see planSectionIndex) -- and SelectedSolutionSections still posts select-solution, which is what
  // prices it. Three of the four fetch effects below bail on isLinked; the priced-plan one deliberately does
  // not, it redirects. Read them as "does not choose", not "does not talk to the server".
  //
  // Read from window.user_quote_nav_sections rather than re-derived from part types: the pairing is decided
  // server-side (linkedNavSection.js), and every select-solution response refreshes that global, so a section
  // that becomes linked mid-session is seen on the next render.
  //
  // Each effect bails individually rather than the component early-returning: hooks must run unconditionally,
  // so "does not fetch" has to be said at every fetch. The legacy listing does the same
  // (LayoutNavSectionCombinationList's isLinkedSection).
  // The row capacity of the surface this is rendered on -- the wizard's modal fits three cards across, the page
  // four. Below it the cards are centred in the spare width.
  const cardsPerRow = inWizard ? WIZARD_CARDS_PER_ROW : PAGE_CARDS_PER_ROW;

  const navSectionsForLink = window.user_quote_nav_sections;
  const isLinked = isLinkedSection(navSectionsForLink, navSectionIndex);
  const linkedParent = linkedParentOf(navSectionsForLink, navSectionIndex);

  // WHOSE MANUFACTURING LINES TO ASK ABOUT. A linked section's plan belongs to the section that LEADS it, so the
  // plan is looked up against the PARENT's index: asking for this section's own would find nothing, because it
  // has no manufacturing lines of its own -- which is the whole reason it inherits.
  const planSectionIndex = isLinked && linkedParent ? linkedParent.sort_order : navSectionIndex;

  const [listing, setListing] = useState(null);
  // WHY THERE IS NO LISTING, when there is none. The section's manufacturing lines are worked out on the
  // manufacturing_cost_calculator queue now, so an ask can END in a failure -- the work raised, or (in
  // development) nothing is listening on that queue at all -- and a failure arrives here as no listing, which
  // is EXACTLY what "still loading" looks like. Drawn as one, it is a stage that never fills: the guided setup
  // wizard sat on an empty "Select a manufacturing line for Carton" with a disabled Next, no cards, no message
  // and no end, while the reason went to a console nobody had open. So the failure is held, said, and can be
  // asked again -- see #renderListingUnavailable.
  const [listingUnavailable, setListingUnavailable] = useState(null);
  // Once a search actually STARTS, every filter locks -- disabled and dimmed -- until the answer lands, so a
  // viewer cannot fire a fresh find + score while one is already running. Ticking before Search leaves it
  // unlocked (the ticks are cheap, local state).
  const [isRefetchingListing, setIsRefetchingListing] = useState(false);
  // THE VIEWER GAVE UP ON THIS SEARCH. The find + score can take tens of seconds, and while it runs the filters
  // are locked -- so without a way out, wanting a different bobbin or machine type means waiting for an answer
  // nobody wants. Cancelling stops the requests, unlocks the block and leaves whatever was already found on
  // screen; the next committed tick starts a fresh search and clears this.
  const [searchCancelled, setSearchCancelled] = useState(false);
  // How the Cancel button reaches the two passes it has to stop. Each pass writes its own stopper here while it
  // is in flight -- the listing fetch marks itself superseded, the score batch aborts its requests and empties
  // the queue -- because both live inside effects the button cannot see into.
  const cancelListingRef = useRef(null);
  const cancelScoringRef = useRef(null);
  // A SEARCH THAT ANSWERS THE SAME AS THE LAST ONE STILL HAS TO LOOK LIKE AN ANSWER. Two searches can list the
  // very same manufacturing lines -- bobbin on and bobbin off are found over different material sheets, but the
  // same presses and cutters run both -- so the results settle back to a screen identical to the one before the
  // press, and the honest conclusion from watching that is that the filter does nothing. The results dim for
  // half a second as they land instead: something arrived, and it happens to be this. See the flash effect.
  const [resultsJustRefreshed, setResultsJustRefreshed] = useState(false);
  // Set by Search and read when the results settle, so ONLY an asked-for search flashes -- not the section's own
  // first listing, and not a cancelled one (nothing arrived to show).
  // WHAT THIS SEARCH IS WAITING FOR: the counters as they stood when Search was pressed, or null when nothing
  // was asked for. The blink fires when BOTH have moved past them -- a listing has landed for this press and its
  // scoring pass has finished -- because either alone is a half-answer: the moment the listing lands, the scores
  // on screen are still the previous search's and read as complete, and blinking there says "here is your
  // answer" over numbers about to be thrown away.
  const awaitingSearchResultsRef = useRef(null);
  // How many listings have LANDED for this section (see the listing fetch). The blink watches this rather than
  // the listing itself, because a Search that answers with the listing already on screen changes no state at
  // all -- same object from the response cache, no scoring pass, no request.
  const [listingLandedCount, setListingLandedCount] = useState(0);
  // How many scoring passes have finished for this section. Counted for the same reason as the landing above:
  // re-scoring the same lines at the same quantity ends with the very numbers already on screen, so nothing
  // about the state itself says a pass completed.
  const [scoringCompletedCount, setScoringCompletedCount] = useState(0);
  // The reductions switched OFF for the SECTION: ['smallest_die_cutting_machine_of_each_type'].
  // Every one is ON by default -- switching one off is an administrator asking to SEE what it hides, so the
  // reduction is never a silent one.
  //
  // Restored from the address bar (like the picked plan below), so the section comes back on the filters it was
  // left on rather than on the server's defaults -- see manufacturingLineListUrlState.
  const [disabledFilters, setDisabledFilters] = useState(() => readSectionDisabledFilters(navSectionIndex));
  // The machine types ticked for the SECTION, by filter key: { printing_machine_types: ['offset'] }.
  // Absent means "not spoken yet" -- the server applies the filter's own default (Offset for printing;
  // everything but Digital for die-cutting), so the defaults live in ONE place rather than being duplicated here.
  const [selectedTypes, setSelectedTypes] = useState(() => readSectionMachineTypes(navSectionIndex));

  // THE FILTERS ARE ASKED FOR, NOT SUBMITTED. The checkboxes above change the edited values at once so the ticks
  // respond instantly, but nothing is found or scored until the viewer presses Search: a find + score is tens of
  // seconds of server work, and running one per tick (or one per burst, on a timer that fires while the viewer
  // is still deciding) spent it on filter sets nobody asked about. So the LISTING works over the COMMITTED
  // filters, which catch up to the edited ones only on Search -- and a section switch, whose listing cannot wait
  // to be asked for. `pendingFilterKey` is a stable string of the edited filters; the listing fetch and the
  // score/selection reset both key off `committedFilterKey`.
  //   NOTE: the bobbin is the ONE exception. Its material-sheet recompute (the "Chosen material dimensions"
  //   line) fires straight away from the parent's onChange -- only the LISTING half of a bobbin change waits for
  //   Search. See LayoutAndQuoteBuilderNavSection#fetchDefaultMaterialValues.
  //   The area-ratio input (below) is not on it either -- it commits on blur / Enter, its own coarse commit, and
  //   drives the fetches directly as areaRatioPercent.
  const pendingFilterKey = JSON.stringify({ bobbinEnabled, disabledFilters, selectedTypes });
  const [committedFilterKey, setCommittedFilterKey] = useState(pendingFilterKey);
  // Bumped by every Search press, and a trigger of the listing fetch in its own right -- so pressing Search
  // AFTER a cancel runs the same filters again, which committedFilterKey alone (unchanged) could not ask for.
  const [searchRunNumber, setSearchRunNumber] = useState(0);
  const committedSectionRef = useRef(navSectionIndex);
  // The area-ratio cap the admin is experimenting with, as a percent. areaRatioPercent is the COMMITTED value
  // that drives the fetches (the listing, the solve and the scoring all reduce against it, so a plan is always
  // listed and solved against the same cap); areaRatioInput is the text shown while typing, committed on blur /
  // Enter -- not per keystroke, because each change re-runs a full listing and its heavy per-line scoring.
  // Restored from the address bar too, so the listing, the solve and the scoring come back against the very cap
  // they were left on -- a plan picked under a tuned cap is only meaningful against that cap.
  const [areaRatioPercent, setAreaRatioPercent] = useState(
    () => readSectionAreaRatioPercent(navSectionIndex) ?? DEFAULT_AREA_RATIO_PERCENT
  );
  const [areaRatioInput, setAreaRatioInput] = useState(() => String(areaRatioPercent));
  // The ManufacturingLineList the viewer picked, by identifier -- the plan whose solutions are shown. null = the
  // listing, nothing picked.
  // Restored from the address bar up front (manufacturing_line_<index>) so a refresh / shared link reopens the
  // picked plan straight away, before the listing has even loaded. A stale value (a plan the listing that lands
  // does not hold) is dropped once the listing is known -- see the stale-restore guard below.
  const [pickedList, setPickedList] = useState(() => readSelectedManufacturingLineList(navSectionIndex));
  const [solutionsLoading, setSolutionsLoading] = useState(false);
  // The picked plan's SOLUTION GROUPS: each is an ARRAY holding ONE solution per part, and is drawn as ONE
  // card. See #buildSolutionGroups.
  const [solutionGroups, setSolutionGroups] = useState(null);
  // The solutions grid, so the ranking pass can reach the cards' data-score as each lands.
  const solutionsGridRef = useRef(null);
  // This section's own subtree, which is what the scroll-to-the-selection below looks for its marked card in.
  // The page mounts one of these at a time (FrankQuoteBuilderPage renders the active section, and hides the tab
  // body outright while the wizard is open), so this is not fixing a collision -- it keeps the search inside
  // what this component drew rather than over the whole document.
  const sectionRootRef = useRef(null);
  // Which arrival (stage + section) the page has already scrolled for, so opening a tab scrolls ONCE -- picking
  // a different card while already there must not yank the page.
  const scrolledForStageRef = useRef(null);
  // The arrival the last render was on, which is how the one above is re-armed: noticing the CHANGE here keeps
  // it in the same pass. A separate effect for it also fired on mount, wiping the mark the first pass had just
  // made -- and a second watch then started as soon as the cards landed.
  const lastArrivalRef = useRef(null);
  // The animation frame the settle-watch is waiting on, so it can be called off on unmount.
  const watchFrameRef = useRef(null);
  // How that watch lets go of its listeners, kept so unmount can do it too.
  const watchTeardownRef = useRef(null);
  // The beat between settling and gliding, kept so unmount can call it off.
  const glideTimerRef = useRef(null);
  // Whether the list this stage draws has finished arriving -- written during the render that knows (below,
  // where solutionsLoading and bestScoresReady are both in hand) and read by the scroll effect above.
  const listSettledRef = useRef(false);
  // The BEST (lowest) score of each DISTINCT manufacturing line, keyed by lineKey -- what the cards sort and
  // label by. Empty until all the per-line best-score requests have landed (the progress bar covers the wait).
  // null for a line that produces no solution (its list sorts last).
  const [bestScoreByLine, setBestScoreByLine] = useState({});
  // The lines whose first attempt was lost and which are being asked again. They are recorded in
  // bestScoreByLine as answered so the rest of the listing can finish, and held here so their OWN card shows a
  // placeholder rather than the em dash that means "cannot be manufactured" -- which is what a lost answer used
  // to be drawn as.
  const [retryingLines, setRetryingLines] = useState(new Set());
  // THE CHOSEN SOLUTION -- an ARRAY holding one solution per part (a solution GROUP, which is what one card
  // is), or null while none is chosen. Choosing one is what moves the section from "how would this be made" to
  // "what does it cost": it is stored on the section and the cost sections are priced from it.
  //
  // Held WITH THE SECTION IT BELONGS TO, and read back only for that section. The page switches section
  // WITHOUT remounting this component, and a child's effect runs before its parent's -- so a bare array would
  // be visible to children for one render under the NEW index while still holding the PREVIOUS section's
  // choice, and SelectedSolutionSections would post it there. That is not a wasted request: the endpoint
  // writes `lists` straight over the section's selected_lists, so section 0's solution would be saved onto
  // section 1 while the page, one effect later, correctly shows section 1 as unpriced.
  //
  // Restored from what the page was rendered with, so a refresh reopens on the priced section.
  const [selection, setSelection] = useState(() => ({
    index: navSectionIndex,
    solutions: restoreSelectedSolutions(navSectionIndex)
  }));

  // THE PRICED PLAN'S CARD, recovered for the refresh path. On a refresh the listing is re-fetched with the
  // DEFAULT machine-type filters, which drop a plan whose machines are off by default (a Flatbed UV press + a
  // Digital cutter) -- so the priced view can no longer find its own plan in `listing`. It fetches that plan by
  // identifier from the UNFILTERED priced endpoint into here. null while nothing is priced, or when the listing
  // already holds the plan (a fresh in-session select, where the filters the viewer ticked to surface it are
  // still set).
  const [pricedPlanData, setPricedPlanData] = useState(null);

  // The solution card the user has MARKED in a 'solution' view (wizard stage or the page's Solution tab). Held
  // separately from `selection` on purpose: setting `selection` would switch this component to the priced view
  // (and its solutions-fetch guard would clear the grid), whereas we want the solution grid to stay on screen
  // with the marked card highlighted. The wizard commits it on its own "Done"; the page's Pricing tab commits
  // it (see the pricing commit effect). Restored from the already-priced section on mount so revisiting a
  // completed section re-marks its card.
  const [markedSolution, setMarkedSolution] = useState(
    () => (stageView === 'solution' ? restoreSelectedSolutions(navSectionIndex) : null)
  );

  const selectedSolutions = selection.index === navSectionIndex ? selection.solutions : null;

  // The setter the children are handed. They pass an array or an updater, exactly as they would to a plain
  // useState setter (the shared resetSelectedListHandler passes `() => []`); filing it against the section on
  // screen is this component's business, not theirs.
  const setSelectedSolutions = useCallback((value) => {
    setSelection((previous) => {
      const current = previous.index === navSectionIndex ? previous.solutions : null;
      return {
        index: navSectionIndex,
        solutions: typeof value === 'function' ? value(current) : value
      };
    });
  }, [navSectionIndex]);

  // Mark a solution card (does NOT set `selection`, see #markedSolution). Clicking the marked card again
  // unmarks it. Used by both the wizard solution stage and the page's Solution tab.
  const markSolution = useCallback((group) => {
    setMarkedSolution((previous) => (groupUuids(previous) === groupUuids(group) ? null : group));
  }, []);

  // Report the marked plan / marked solution up to the wizard (FrankSetupWizard) as they change, so its
  // Next / Done buttons enable and disable in step with the selection on screen. No-ops on the page tabs (the
  // callbacks are absent there).
  //
  // A plan is reported ONLY once the listing has loaded and still OFFERS it. `pickedList` is restored from the
  // manufacturing_line_<index> URL param before the listing loads (so a shared link reopens the picked plan),
  // and reporting that unconfirmed value let a STALE param enable the wizard's Next on an empty, still-loading
  // line stage -- advancing the guided flow with nothing really chosen and no valid plan for the solution stage.
  // Gating on the loaded listing makes the wizard's Next track the card the listing actually shows selected:
  // disabled while the listing loads, disabled for a stale plan the listing dropped, enabled once a real plan
  // (restored or clicked) is on screen. The stale-restore guard below clears such a pick from `pickedList` too,
  // but only after the listing lands; this keeps the wizard's Next honest in the meantime.
  useEffect(() => {
    if (!onWizardPickedChange) { return; }
    const offeredByListing = Boolean(listing) && Boolean(pickedList)
      && (listing.manufacturing_line_lists || []).some((list) => list.identifier === pickedList);
    onWizardPickedChange(offeredByListing ? pickedList : null);
  }, [pickedList, listing, onWizardPickedChange]);

  // THE SOLUTION CHOSEN ON SCREEN -- ONE value, read by BOTH the card highlighting and the wizard's Next/Done,
  // so the two can never disagree. It is the in-session mark, or -- on a reopened priced section holding no
  // mark -- the section's own priced solution, which is what the card draws as "Selected".
  //
  // Reporting only `markedSolution` here is what left the reopened wizard drawing a card "Selected" with its
  // Next DISABLED: `markedSolution` restores from the priced section only when the component MOUNTS on the
  // solution view, and the wizard's line and solution stages share one instance (same key), so arriving from
  // the line stage never restores it -- while the card, reading the fallback below, showed it as chosen.
  //
  // The priced solution stands in ONLY while it is still on screen: either the solve returned nothing and it is
  // itself the card shown (the priced fallback), or it is one of the solved groups. Pick a DIFFERENT
  // manufacturing line and the section re-solves, and the old priced solution is not among the new plan's
  // solutions -- the card already highlights nothing there, and reporting it anyway would let the wizard's Done
  // persist a solution the user can no longer see. Same discipline as the picked plan above, which is reported
  // only while the listing still OFFERS it.
  // "Still on screen" is read as the CARD reads it, including while the solve is in flight: the grid is a
  // skeleton then, showing no solution at all, so a priced solution must not count as chosen -- otherwise Done
  // pressed during the solve would persist the OLD solution while the new plan's cards were still loading.
  const solvedGroups = solutionGroups || [];
  const pricedSolutionOnScreen = hasSelection(selectedSolutions) && !solutionsLoading
    && (solvedGroups.length === 0
      || solvedGroups.some((group) => groupUuids(group) === groupUuids(selectedSolutions)));
  const chosenSolution = hasSelection(markedSolution)
    ? markedSolution
    : (pricedSolutionOnScreen ? selectedSolutions : null);

  useEffect(() => {
    if (onWizardSelectionChange) { onWizardSelectionChange(chosenSolution); }
  }, [chosenSolution, onWizardSelectionChange]);

  // THE PAGE'S PRICING TAB COMMITS THE MARKED SOLUTION. The wizard commits on its Done; on the page there is no
  // Done, so arriving at the Pricing tab is what commits the marked solution -- setting `selection` renders the
  // priced view, which posts select-solution and prices the section. It commits whenever the mark DIFFERS from
  // what is priced, so re-picking a different solution on the Solution tab re-prices too.
  useEffect(() => {
    if (stageView !== 'pricing' || inWizard) { return; }
    if (hasSelection(markedSolution) && groupUuids(markedSolution) !== groupUuids(selectedSolutions)) {
      setSelectedSolutions(markedSolution);
    }
  }, [stageView, inWizard, markedSolution, selectedSolutions, setSelectedSolutions]);

  // OPENING A TAB THAT ALREADY HAS A CHOICE SCROLLS TO IT. A section with a picked manufacturing line or a
  // marked solution can hold rows of cards, and the chosen one sorts wherever its score puts it -- so the tab
  // opened at the top of a list with nothing on screen saying which card is the answer, and the reader had to
  // hunt for the outlined one.
  //
  // ONCE per arrival, tracked in scrolledForStageRef: the mark also changes while the reader is on the tab
  // (that is what picking is), and scrolling then would pull the page out from under the click. Coming back to
  // a stage is a fresh arrival and lands on the choice again -- see #scrollToSelectedCard.
  //
  // NOT while it is already on screen. Entering a tab whose selection is visible and having the page glide
  // anyway reads as the page moving for its own reasons; #cardNeedsScrolling asks first.
  //
  // The effect deliberately has NO dependency list. The cards arrive asynchronously -- the listing, then the
  // solve -- so there is no one value that means "the marked card is in the document now"; running after every
  // render and stopping at the ref costs a querySelector until the card appears, and nothing after that.
  // HAS THE SOLUTION LIST FINISHED RANKING? Its cards are re-ordered by a DOM sorter on a 200ms interval
  // (#sortSolutionsByScoreAndDrawStars), because each card fetches its OWN score and they land one at a time --
  // so the marked card MOVES until the last one is in. Measured on the reported quote, arriving straight at the
  // Solution tab: the marked card sat at the top until the ranking ran and dropped it 1765px, long after its
  // own pixels had stopped moving. No React state says "ranked", but the cards do: data-score is 0 until a
  // card's score lands (LayoutNavSectionCombinationListList).
  //
  // Read here rather than beside the other readiness flags because it reads the DOM, which is only the drawn
  // one inside an effect.
  const solutionsRanked = () => {
    const grid = solutionsGridRef.current;
    if (!grid) { return false; }

    const cards = Array.from(grid.children);
    return cards.length > 0 && cards.every((card) => parseInt(card.getAttribute('data-score'), 10) > 0);
  };

  // IS THE LIST FINISHED -- both arriving and ordering itself? Until it is, the marked card is still liable to
  // move: the Solution tab draws what it has while the solve is out, and the cards re-order themselves as the
  // scores land. Measured on the reported quote, arriving straight at the Solution tab: the marked card sat at
  // the top until the ranking dropped it 1765px, long after its own pixels had stopped moving.
  const listReady = () => {
    if (!listSettledRef.current) { return false; }

    return stageView === 'solution' ? solutionsRanked() : true;
  };

  const cardNeedsScrolling = (card) => {
    const box = card.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    return box.top < 0 || box.bottom > viewportHeight;
  };

  const glideTo = (card) => {
    if (!cardNeedsScrolling(card)) { return; }

    // A reader who has asked for less motion gets the same landing without the glide, the way Frank's own
    // stylesheet answers that preference for its skeletons and progress bars.
    const stillness = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    card.scrollIntoView({ behavior: stillness ? 'auto' : 'smooth', block: 'center' });
  };

  // NOT ON THE CARD'S FIRST FRAME. A solution card ARRIVES SMALL and grows as its score panel and pictures land
  // -- measured on the reported section (work/frank_scroll_card_growth_probe.mjs): 122px tall at 699ms, 864 at
  // 820, settling at 889 by 953. Both questions this asks are answered wrong against that first frame: a
  // 122px card sits inside the viewport, so "is it already on screen?" says yes and nothing scrolls, and
  // centring it would centre a card that is about to be seven times taller.
  //
  // So watch its height until it stops changing, then decide (SETTLED_FRAMES / SETTLE_FRAME_LIMIT).
  const glideWhenSettled = (card) => {
    if (typeof window.requestAnimationFrame !== 'function') { glideTo(card); return; }

    // THE READER'S OWN SCROLLING WINS -- but only the READER's. Settling takes a moment (about a second on the
    // Solution stage, where the cards are fetched and then grow), and someone who starts moving in that window
    // has said where they want to be; gliding then takes the page off them.
    //
    // Read from their INPUT, never from window.scrollY. Comparing scroll positions cannot tell a person from
    // the page itself: arriving at the Solution tab from Manufacturing Line starts this watch while the glide
    // to the manufacturing line card is STILL ANIMATING (measured on the reported quote: scrollY climbing
    // 26 -> 51 -> 67 -> 84 -> 103 ...), so every such arrival read as "the reader scrolled" and stood down --
    // which is exactly the tab that was reported as not scrolling. A page shrinking under a fixed scroll
    // position clamps it the same way.
    let readerMoved = false;
    const readerInput = (event) => {
      // A keystroke only counts if it is one that scrolls -- typing in the machine filters must not stand this
      // down.
      if (event.type === 'keydown' && !READER_SCROLL_KEYS.includes(event.key)) { return; }
      readerMoved = true;
    };
    READER_INPUT_EVENTS.forEach((name) => window.addEventListener(name, readerInput, { passive: true }));
    const stopListening = () => {
      READER_INPUT_EVENTS.forEach((name) => window.removeEventListener(name, readerInput));
    };
    watchTeardownRef.current = stopListening;

    const stopWatching = () => { watchFrameRef.current = null; };
    const release = () => {
      stopListening();
      watchTeardownRef.current = null;
    };
    const finish = () => { stopWatching(); release(); };

    // THE BEAT BEFORE THE PAGE MOVES. Two things want it. The reader has just clicked a tab, and a page that
    // glides in the same instant reads as having jumped rather than travelled. And the ordering is not final
    // the moment the last score lands: the cards are re-ordered by a sorter on a 200ms interval
    // (#sortSolutionsByScoreAndDrawStars), so a card can still be moved one tick AFTER its own box went still.
    // GLIDE_DELAY_MS covers one full tick, and the position is measured when it fires rather than now, so the
    // glide goes to where the card ENDED UP.
    //
    // The reader can still take it back during the beat -- their input is listened for until it fires -- and a
    // card taken out of the document in the meantime is left alone.
    const glideAfterTheBeat = () => {
      stopWatching();
      glideTimerRef.current = window.setTimeout(() => {
        glideTimerRef.current = null;
        release();
        if (!readerMoved && card.isConnected) { glideTo(card); }
      }, GLIDE_DELAY_MS);
    };

    let lastShape = '';
    let stillFrames = 0;
    let framesWatched = 0;
    let framesWaitingForList = 0;

    const watch = () => {
      // THE CARD CAN BE REPLACED UNDER THE WATCH -- a fresh solve re-renders the grid, and the element this was
      // started on is then detached. A detached node measures 0x0, which reads as "settled, and already on
      // screen", so the glide would be skipped for good. Give the arrival back instead, and the next render
      // starts a watch on the card that replaced it.
      if (!card.isConnected) {
        scrolledForStageRef.current = null;
        finish();
        return;
      }

      if (readerMoved) { finish(); return; }

      // Nothing is measured until the list has stopped changing under the card -- see #listReady. Waiting is
      // bounded separately from settling, because a slow solve is not a card that will not settle.
      if (!listReady()) {
        stillFrames = 0;
        framesWaitingForList += 1;
        if (framesWaitingForList >= LIST_READY_FRAME_LIMIT) { finish(); return; }
        watchFrameRef.current = window.requestAnimationFrame(watch);
        return;
      }

      // WHERE IT IS as well as HOW BIG IT IS. A card can reach its final height while still MOVING, because
      // the cards above it are growing -- and gliding then lands where the card used to be. Measured on the
      // reported quote: the direct arrival scrolled 291px to a card that ended up 1737px further down.
      const box = card.getBoundingClientRect();
      const shape = `${Math.round(box.top)}:${Math.round(box.height)}`;
      stillFrames = shape === lastShape ? stillFrames + 1 : 0;
      lastShape = shape;
      framesWatched += 1;

      if (stillFrames >= SETTLED_FRAMES || framesWatched >= SETTLE_FRAME_LIMIT) {
        glideAfterTheBeat();
        return;
      }
      watchFrameRef.current = window.requestAnimationFrame(watch);
    };

    watchFrameRef.current = window.requestAnimationFrame(watch);
  };

  // A watch outlives the render that started it, so it has to be called off when the section goes away -- a
  // page that has moved on must not be scrolled by a card that is no longer on it.
  useEffect(() => () => {
    if (watchFrameRef.current && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(watchFrameRef.current);
    }
    if (glideTimerRef.current) { window.clearTimeout(glideTimerRef.current); }
    if (watchTeardownRef.current) { watchTeardownRef.current(); }
  }, []);

  const scrollToSelectedCard = () => {
    // The stage AND the section: the page switches section without remounting this component, so the stage
    // alone would call an arrival at another section's list "already done".
    const arrival = `${stageView}:${navSectionIndex}`;

    // ARRIVING SOMEWHERE ELSE RE-ARMS THIS -- including coming BACK to a stage that was scrolled for earlier,
    // which is a fresh arrival and should land on the choice again. Noticed here rather than in an effect of
    // its own, before the stage is even looked at, so a trip through Pricing counts as leaving.
    if (lastArrivalRef.current !== arrival) {
      lastArrivalRef.current = arrival;
      scrolledForStageRef.current = null;
    }

    // The PAGE's tabs only. The guided setup wizard walks the same stages inside a modal (it passes stageView
    // too), but there the reader is choosing as they go rather than returning to a choice already made, and the
    // modal has its own scrolling to answer for.
    if (inWizard) { return; }
    if (stageView !== 'line' && stageView !== 'solution') { return; }
    if (scrolledForStageRef.current === arrival) { return; }

    // Whether the LIST is finished is asked inside the watch (#listReady), not here: it can become true without
    // this component re-rendering at all -- a card writes its own score into its OWN state, so the parent is
    // never told and this pass would not run again to notice.
    const card = sectionRootRef.current && sectionRootRef.current.querySelector('[data-selected-card]');
    if (!card) { return; }

    // Marked as done as the watch STARTS, not when it scrolls: every render until the card settles would
    // otherwise start a watch of its own.
    scrolledForStageRef.current = arrival;
    glideWhenSettled(card);
  };

  useEffect(scrollToSelectedCard);

  // THE PLAN THE CHOSEN SOLUTION WAS MADE ON, derived from the chosen CARDS rather than read from `pickedList`.
  // A list's identifier is exactly its manufacturing lines' identifiers in cover order
  // (Frank::ManufacturingLineList#identifier) and every card carries the line it was solved on, so the plan is
  // a property of the selection itself. It has to be: `pickedList` is page state, and the two ways a priced
  // section reaches the screen without one are both normal -- a link straight into a priced section never
  // picked anything, and switching section clears the pick while the selection is restored from the server.
  //
  // ALL OR NOTHING: a card with no identifier (a selection made on the legacy page, which stamps none) makes
  // the whole thing null. Dropping the empty ones instead would turn "I know some of the lines" into a
  // complete-looking identifier -- and on a two-part section that shortened string can MATCH a different,
  // single-line plan in the listing, which would then be drawn as the plan and scoped the scoring.
  const pricedLineIdentifiers = hasSelection(selectedSolutions)
    ? selectedSolutions.map((solution) => solution.diecutting_result.manufacturing_line_identifier)
    : [];
  const pricedListIdentifier = pricedLineIdentifiers.length > 0 && pricedLineIdentifiers.every(Boolean)
    ? pricedLineIdentifiers.join('|')
    : null;

  // On a reopened PRICED page section, solve for the plan the section is ACTUALLY priced on -- not a stale
  // manufacturing_line_<index> left in the URL by an earlier pick. Runs ONCE, so a later in-session pick is left
  // alone. This keeps the Solution tab listing the priced plan's solutions (with the priced one selected) on
  // reload, and the Manufacturing Line tab highlighting the right plan. Not in the wizard, which drives its own
  // pick and deliberately ignores the restored selection.
  const syncedPricedPickRef = useRef(false);
  useEffect(() => {
    // Page tabs only: the legacy stacked view (no stageView) and the wizard (inWizard) keep their own behaviour.
    if (!stageView || inWizard || syncedPricedPickRef.current) { return; }
    if (!hasSelection(selectedSolutions) || !pricedListIdentifier) { return; }
    syncedPricedPickRef.current = true;
    if (pickedList !== pricedListIdentifier) { setPickedList(pricedListIdentifier); }
  }, [stageView, inWizard, selectedSolutions, pricedListIdentifier, pickedList]);

  // THROW AWAY A SELECTION ITS MANUFACTURING LINE NO LONGER MATCHES. Called when the user picks a plan other
  // than the one the current selection is priced from; a no-op when nothing is selected, so browsing the
  // listing before choosing anything costs nothing.
  //
  // The SAME reset the section's own "start again" does (LayoutCombinationsFinder::ResetSelectedLists via
  // resetSelectedListHandler), rather than a second way of unselecting: it clears selected_lists server-side,
  // refreshes window.user_quote_nav_sections, and publishes navSectionsReset -- which is what takes the linked
  // sections (a corrugated section inherits this one's solution) and the solution_<index> address bar
  // parameters with it. Local state is cleared too, so nothing on screen outlives the plan it belonged to.
  // NOTHING IS CLEARED UNTIL THE SERVER HAS ACTUALLY CLEARED IT. A reset that fails and a screen that has
  // already forgotten the selection is the worst of both: the section reads as unpriced while the quote still
  // holds the old solution, and the next reload contradicts it -- restoring that solution AND, with it, the
  // pick the user had just changed, which is the very bug this discard exists to stop. So the local state goes
  // in the success callback, and a failure is reported rather than swallowed (a 500 tracks itself inside
  // PostRequest; a network failure would otherwise leave no trace at all).
  const discardSelectionForChangedPlan = useCallback(() => {
    if (!hasSelection(selectedSolutions) && !hasSelection(markedSolution)) { return; }

    resetSelectedListHandler({
      index: navSectionIndex,
      setSelectedList: setSelectedSolutions,
      onSuccess: () => {
        // The priced pick is not synced back over the new one: the selection it would restore is now gone.
        syncedPricedPickRef.current = true;
        setMarkedSolution(null);
        setPricedPlanData(null);
      }
    }).catch((error) => {
      trackEvent('js_error_happened', {
        exception: `[ManufacturingLineLists#discardSelectionForChangedPlan] ${error && error.message ? error.message : error}`,
        nav_section_index: navSectionIndex,
        user_quote_id: window.user_quote_id
      });
    });
  }, [selectedSolutions, markedSolution, navSectionIndex, setSelectedSolutions]);

  // THE ONE PLAN THE PAGE IS ABOUT, if any: the picked one while a solution is being chosen, the priced one
  // once it has been. Everything scoped to "the plan on screen" -- which lines get scored, which lists the star
  // ratings normalize against, which card is drawn -- follows this rather than the pick alone. Scoring did not,
  // which is why a priced section opened from a link scored every plan in the listing for a listing it never
  // shows.
  //
  // Once a solution is CHOSEN it is the derived plan or nothing -- never `pickedList`. The pick is whatever
  // manufacturing_line_<index> happens to say in the address bar, which on a deep link into a section priced
  // from a legacy selection is a plan with nothing to do with the chosen solution.
  // In the WIZARD, the "plan on screen" is always the marked pick -- the wizard drives selection through its own
  // markedSolution and does the pricing itself on Done, so a `selectedSolutions` restored from an already-priced
  // section (a reopened quote) must NOT take over here: doing so scoped scoring to the old plan and, worse,
  // blocked the solutions fetch (see the solve effect), which is why a freshly picked plan showed "no solutions".
  // A LINKED SECTION'S PLAN IS ALWAYS THE INHERITED ONE, on every tab. The `!stageView` condition below exists
  // because a tabbed section is mid-flow and its pick drives the view; a linked section never picks, so
  // pickedList is null for it and reading it would leave every tab with no plan at all -- which is what showed
  // the solution card under "Manufacturing Line" and no manufacturing line anywhere.
  const planOnScreen = isLinked
    ? pricedListIdentifier
    : ((hasSelection(selectedSolutions) && !stageView) ? pricedListIdentifier : pickedList);

  // WHAT SCORING IS FOR, and so WHAT COUNTS AS SCORED: every manufacturing line on the Manufacturing Line
  // stage, where the user compares them all; just the plan on screen everywhere else, where the other plans are
  // not drawn and scoring them made a deep link slow for nothing.
  //
  // THE STAGE DECIDES IT, so it is derived out here and taken as the best-score effect's dependency. Read only
  // inside that effect, a stage switch that WIDENS the scope did not re-run it -- and arriving at Manufacturing
  // Line from Pricing or Solution is exactly that switch, the one the guided setup wizard leaves the user to
  // make (it closes onto Pricing). The readiness test follows the stage (see listsOnScreen), so the listing then
  // waited for scores nothing had asked the server for: "Scoring manufacturing lines…" with no request in
  // flight, for as long as the user was willing to look at it.
  //
  // It also stops a pick made ON the listing from cancelling the pass that is scoring it: the scope stays null
  // there, so the batch runs to the end instead of being aborted and started again.
  const planScope = stageView === 'line' ? null : planOnScreen;

  // THE SOLUTION GROUPS -- what a card actually is.
  //
  // A ManufacturingLineList makes the WHOLE section, and it can do that with more than one manufacturing line:
  // a shirt box's plan is "Kapak on the KBA + BOBST, Kutu on the 70x100 Pedal". Those two are not two answers,
  // they are two HALVES of one answer -- so one card must show a solution for EACH part, and the card is the
  // pair. Listing each part's solutions on their own says "here are some covers and some trays" and leaves the
  // reader to do the pairing.
  //
  // So the groups are the CARTESIAN PRODUCT across the plan's parts, copied from the legacy listing's
  // handlePartsMode:
  //     one part  -> [[s1], [s2], ...]                 (a group of one; every solution is already whole)
  //     two parts -> [[kapak1, kutu1], [kapak1, kutu2], ...]
  //
  // The product is taken over the cheapest TOP_N_PER_PART of each part, because the full product is unreadable
  // (35 Covers x 30 Boxes is 1,050 cards). Cheapest needs the score -- which now arrives WITH the solution
  // (score_data, computed server-side by Frank::SolutionScore), so this is a plain SYNCHRONOUS sort, no
  // per-solution round trip. See combineSolutionGroups for what a cost cutoff systematically hides.
  //
  // WARMING THE CACHE (not an XHR): the reused legacy solution card reads its score from the shared score cache
  // (LayoutNavSectionCombination#getScore checks the cache before fetching). Priming that cache from the
  // embedded score_data is what lets Frank reuse the legacy card WITHOUT its per-card fetch to
  // /layouts-nav-section-combination-score -- the card finds every score already answered.
  //
  // A part with NO solutions collapses the whole product to nothing, correctly: if the Kutu cannot be made on
  // its assigned machine, the PLAN cannot make the section, however many ways the Kapak has.
  const buildSolutionGroups = (data) => {
    const groups = (data && data.solutions) || [];

    // Prime the score cache from the embedded score_data, so the legacy card renders every solution with no XHR.
    groups.forEach((group) => group.solutions.forEach((solution) => {
      if (solution.score_data) { setInCache(solution.diecutting_result.uuid, solution.score_data); }
    }));

    const perPart = groups.map((group) => group.solutions).filter((list) => list.length > 0);

    // Every part must be makeable, or the plan makes nothing.
    if (perPart.length === 0 || perPart.length !== groups.length) { return []; }

    // The complete ways to make the section -- the cartesian across each part's cheapest TOP_N_PER_PART,
    // cheapest COMBINED plan first. See combineSolutionGroups.
    return combineSolutionGroups(perPart, TOP_N_PER_PART);
  };

  const toggleFilter = (key) => setDisabledFilters((previous) => (
    previous.includes(key) ? previous.filter((each) => each !== key) : [...previous, key]
  ));

  // Tick or untick one machine type. The CURRENT selection comes from the server (it applied the defaults), so
  // the first tick starts from what is actually on screen rather than from an empty set.
  const toggleMachineType = (key, type, currentlySelected) => setSelectedTypes((previous) => {
    const selected = previous[key] || currentlySelected;
    const next = selected.includes(type) ? selected.filter((each) => each !== type) : [...selected, type];
    return { ...previous, [key]: next };
  });

  // A SECTION SWITCH commits at once: its listing is not something the viewer asked for with Search, it is what
  // the section IS, so it must not sit behind a button. Every other commit comes from Search (#runSearch).
  useEffect(() => {
    if (committedSectionRef.current === navSectionIndex) { return; }

    committedSectionRef.current = navSectionIndex;
    setCommittedFilterKey(pendingFilterKey);
  }, [navSectionIndex, pendingFilterKey]);

  // Commit the typed area-ratio percent -- on blur or Enter, so the expensive re-list / re-score runs once the
  // admin has finished a value, not on every keystroke. A blank or non-numeric entry (or a negative, which is
  // not a "percent larger") is rejected: the field snaps back to the committed value and nothing re-fetches. A
  // valid value that has not changed does not re-fetch either -- React bails on the same state.
  const commitAreaRatio = () => {
    const parsed = Number(areaRatioInput);
    if (areaRatioInput.trim() === '' || Number.isNaN(parsed) || parsed < 0) {
      setAreaRatioInput(String(areaRatioPercent));
      return;
    }
    setAreaRatioPercent(parsed);
    setAreaRatioInput(String(parsed));
  };

  // The bobbin checkbox changes the section's material sheets, and the manufacturing lines are found over
  // those sheets -- so re-fetch when the bobbin commits, or the lines shown would be the ones for the previous
  // sheets. A machine-type or reduction toggle re-fetches for the same reason (the reduction happens
  // server-side, on the found lines) -- all three land here through committedFilterKey, on Search.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined) { return; }
    // A LINKED SECTION HAS NO LISTING AND ASKS FOR NONE -- its manufacturing line comes from the section that
    // leads it. It is given an EMPTY listing rather than left at null, because null means "not fetched yet" and
    // everything downstream waits on it; this section is not waiting, it is finished. Its own pricing still
    // renders below, which is the point: only the line and the solution are shared, not the prices.
    if (isLinked) { setListing(NO_LISTING_OF_ITS_OWN); return; }

    const query = new URLSearchParams();
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      // A query string cannot carry an empty array, and "absent" already means "use the default" -- so an
      // empty selection is sent as one blank entry, which the server reads as "nothing ticked".
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The admin's area-ratio cap, so the listing is reduced against the same value the solve and scoring use.
    query.set('area_ratio_percent', areaRatioPercent);
    // The listing itself is count-independent (it lists which plans CAN make the section, not their cost), but
    // count is sent so a quantity change re-fetches it and so re-mints the `listing` object the best-score effect
    // keys off -- which is what re-triggers the (count-dependent) scoring. It also keeps the cache key uniform:
    // count rides in the URL on all three fetches. The server ignores it here.
    setCountParam(query, count);
    const queryString = query.toString();

    // superseded guards against out-of-order responses: a slow earlier fetch must not land its (stale) listing
    // after a later one -- which would then drive the best-score batch over sheets it was not listed with. It
    // also guards the lock: only the LATEST fetch clears it, so a superseded early answer cannot unlock the
    // filters while the real re-list is still running.
    let superseded = false;
    setIsRefetchingListing(true);
    // A committed change starts a NEW search, so whatever was cancelled before is over -- the block locks again.
    setSearchCancelled(false);
    // ...and so is whatever the last one failed with. Held until here rather than cleared when the failure is
    // drawn, so the message stays on screen until something is actually being done about it.
    setListingUnavailable(null);
    // What Cancel does to this pass: the answer still arrives (fetch cannot be unsent here), but it is dropped
    // like a superseded one, so a cancelled search cannot land its listing minutes later over whatever the
    // viewer moved on to.
    cancelListingRef.current = () => { superseded = true; setIsRefetchingListing(false); };
    // Through the response cache, keyed by this URL (count is in it): an identical listing the wizard (or an
    // earlier visit) just fetched is reused instead of re-fetched. See ManufacturingLineResponseCache.
    fetchJsonCached(
      `/mcc/${window.user_quote_id}/manufacturing-line-lists/${navSectionIndex}${queryString ? `?${queryString}` : ''}`,
      // WHY it could not be answered, kept for the reader -- see #listingUnavailable. It is reported here
      // rather than inferred from the null below, because only the failing ask carries the server's own
      // reason, and in development that reason is the one thing that says what to do about it.
      { onUnavailable: (error) => { if (!superseded) { setListingUnavailable(error.message); } } }
    )
      .then((data) => {
        if (superseded) { return; }
        if (data) { setListing(data); }
        setIsRefetchingListing(false);
        // A LISTING LANDED, whatever it turned out to be. Counted rather than inferred from the listing itself,
        // because a search that answers with the very listing already on screen changes NOTHING a watcher could
        // key on: the response cache returns the same object, so setListing bails out, the scoring pass skips
        // (it has scored this exact listing), and not one request is sent. That is the whole shape of an
        // unedited Search -- correct, instant, and completely silent, which is what read as "Search does
        // nothing". This counter is what the blink hangs off, so an answer arriving is observable even when the
        // answer is the same one.
        setListingLandedCount((previous) => previous + 1);
      })
      .catch((error) => {
        console.error('Error fetching manufacturing line lists:', error);
        if (superseded) { return; }

        setIsRefetchingListing(false);
        // Nothing landed, so this search has no answer to blink for. Left armed, the blink would fire over the
        // NEXT listing to arrive -- a quantity change, a section switch -- claiming a search nobody pressed.
        awaitingSearchResultsRef.current = null;
      });

    return () => { superseded = true; cancelListingRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the body reads the LATEST filter values, which by
    // the time committedFilterKey changes ARE the committed ones (Search commits them together); so
    // committedFilterKey is the trigger, not bobbinEnabled/disabledFilters/selectedTypes directly.
    // searchRunNumber is a trigger of its own, for a Search that commits the SAME filters (re-running after a
    // cancel). areaRatioPercent is another (committed on blur / Enter). count is a trigger too: a quantity
    // change re-fetches the listing, which re-mints the `listing` object the scoring effect keys off, so the
    // (count-dependent) best scores are recomputed for the new quantity.
  }, [navSectionIndex, committedFilterKey, searchRunNumber, areaRatioPercent, count]);

  // The picked plan's SOLUTIONS: how this plan would actually make the section. Fetched only once a plan is
  // picked, because the solve is the expensive half -- the listing above answers without it, which is why the
  // cards can appear long before any solution does.
  //
  // The same inputs the listing was fetched with are sent, so the plan is solved with the very sheets and
  // filters it was listed with: a plan identifier only means anything against a given listing.
  useEffect(() => {
    // The pricing view does not show the solutions grid, so there is nothing to solve there -- and re-solving
    // when a selection lands would be wasted work. Keep whatever groups were already fetched.
    // SKIPPED, NOT CANCELLED: `stageView` is a dependency of this effect (see the list below), so leaving the
    // pricing view runs it again and the solve this stage declined to make is made on arrival at the Solution
    // tab. Without that, a section whose FIRST render is the pricing view -- which is where closing the guided
    // setup wizard leaves the user (FrankQuoteBuilderPage#closeWizard opens the priced section's Pricing stage)
    // -- never solved at all, and its Solution tab fell through to the priced fallback below and listed the
    // chosen solution ALONE, as the only way this plan makes the section. Measured on quote 2706
    // (work/reproduce_mcc_single_solution_after_wizard.mjs): "Solutions (1)", zero /solutions/ requests, for a
    // plan the server answers with 8.
    if (stageView === 'pricing') { return; }
    // No plan picked, or a solution already CHOSEN: either way there is nothing to solve. Reopening a priced
    // section still had its plan in the address bar, so it fired a full solve whose answer was then never
    // rendered -- frank.contract calls the solve the expensive half of the page.
    //   In a 'line'/'solution' view, selection runs through markedSolution, not selectedSolutions, so a
    //   `selectedSolutions` restored from an already-priced section must NOT block the solve -- otherwise a
    //   plan the user just picked shows "no solutions" because the solve never ran.
    const chosenBlocksSolve = hasSelection(selectedSolutions) && !stageView;
    // A linked section solves nothing of its own -- it inherits.
    if (!window.frank_mode || navSectionIndex === undefined || !pickedList || chosenBlocksSolve || isLinked) {
      setSolutionGroups(null);
      return;
    }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', pickedList);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => query.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) {
        query.append(`${key}[]`, '');
        return;
      }
      types.forEach((type) => query.append(`${key}[]`, type));
    });
    // The same cap the listing used -- a plan is only meaningful against the listing it came from.
    query.set('area_ratio_percent', areaRatioPercent);
    // The quantity the server solves/scores against -- see setCountParam.
    setCountParam(query, count);

    const solveUrl = `/mcc/${window.user_quote_id}/solutions/${navSectionIndex}?${query.toString()}`;

    let superseded = false;
    // ONLY WHEN THERE IS SOMETHING TO WAIT FOR. An answer already in the response cache still resolves through a
    // promise, so flipping this on unconditionally puts the skeleton over a grid whose cards are ready to draw --
    // one blank frame every time the user arrives at the Solution tab from another stage, now that the stage
    // re-runs this effect. It is not only a flicker: the chosen solution is read as "on screen" only while
    // nothing is loading (see pricedSolutionOnScreen), so that frame also drops the card's outline and, in the
    // wizard, disables its Done.
    if (!hasCachedResponse(solveUrl)) { setSolutionsLoading(true); }
    // Through the response cache (keyed by this URL, count included), so the plan the wizard just solved is not
    // solved again when the tab mounts, and a stage change mid-solve joins the request in flight rather than
    // making a second one. The raw solve response is cached; buildSolutionGroups still runs each time (it is a
    // cheap local transform, not the expensive solve).
    fetchJsonCached(solveUrl)
      .then((data) => buildSolutionGroups(data))
      .then((groups) => {
        if (superseded) { return; }
        setSolutionGroups(groups);
        setSolutionsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching solutions:', error);
        if (superseded) { return; }
        setSolutionGroups([]);
        setSolutionsLoading(false);
      });

    return () => { superseded = true; };
    // selectedSolutions is a dependency because the guard above READS it: going back from a priced section has
    // to solve the plan again, and choosing one has to stop solving. count is a dependency because it is sent in
    // the solve URL and changes the solve's scores, so a quantity change must re-solve. stageView is one for the
    // same reason it is read: this effect DECLINES to solve on the pricing view, so the stage moving off it is
    // what has to run the solve -- exactly as the stage decides the scoring scope through planScope. Re-running
    // it costs nothing when the groups are already fetched: fetchJsonCached answers an identical solve URL from
    // the response cache rather than the server.
  }, [pickedList, navSectionIndex, bobbinEnabled, disabledFilters, selectedTypes, areaRatioPercent, selectedSolutions,
    count, stageView]);

  // BEST SCORES -- so a card can sort best-first and show its score. Scoring is heavy (every solution of every
  // line, min per line, no pruning: the cheapest way is not guessable from the layout), so WHERE it runs matters:
  //   - LISTING on screen (no plan picked): score EVERY distinct line, ONCE per listing, so the cards can sort
  //     and star. One request per manufacturing line, each asking a worker until its answer lands (they no
  //     longer solve on the request), all dispatched at once through sectionAnswerQueue -- with
  //     ListingProgressOverlay's modal bar over completed/total.
  //   - ONE plan on screen -- picked, or the plan a chosen solution is priced from (a click, or a refresh /
  //     deep link straight into either view): score ONLY that plan's lines. That card needs just its OWN best
  //     score; scoring all ~42 lines there is what made a deep link slow and ran the full progress bar for
  //     nothing. The rest are scored if and when the user goes Back to the listing.
  // A line is fetched ONCE (it is reused across many lists); a list's best is the SUM of its lines' bests.
  //
  // `fullyScoredListingRef` remembers the listing object we have already scored in full, so neither picking a
  // plan from a scored listing nor going Back to it re-scores (the scores are kept) -- only a NEW listing (a
  // filter / section change) or a Back from a deep link (never fully scored) triggers the full pass.
  const fullyScoredListingRef = useRef(null);
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined || !listing) { return undefined; }
    // A linked section scores nothing of its own -- it has no listing to rank.
    if (isLinked) { return undefined; }
    if (fullyScoredListingRef.current === listing) { return undefined; }

    const lists = listing.manufacturing_line_lists || [];
    // planScope is the stage's own scope (see its definition): null on the line stage, where the user compares
    // EVERY manufacturing line, so a pick restored from the URL does not narrow scoring to itself and leave the
    // other cards score-less.
    // NOT "the picked plan": it may equally be the plan a chosen solution is priced from, which is no pick at
    // all. What it always is, is the one plan on screen.
    const planOnScreenList = planScope ? lists.find((list) => list.identifier === planScope) : null;
    // An identifier the listing does not hold (a stale deep link) is dropped by the stale-restore guard; there
    // is nothing to score until it resolves to the listing or a real plan.
    if (planScope && !planOnScreenList) { return undefined; }

    // One plan -> just its lines (merged in, keeping any we already have). Listing -> every line, a fresh
    // full score; on COMPLETION it marks this listing done (below) so picking / Back does not repeat it.
    const scoresWholeListing = !planOnScreenList;
    const scoredLists = planOnScreenList ? [planOnScreenList] : lists;

    const distinctLines = [];
    const seen = new Set();
    scoredLists.forEach((list) => (list.manufacturing_lines || []).forEach((line) => {
      if (line.sub_section_index == null) { return; }
      const key = lineKey(line);
      if (!seen.has(key)) { seen.add(key); distinctLines.push(line); }
    }));
    if (distinctLines.length === 0) { return undefined; }

    // The SAME filter/bobbin params the listing used, so each line is scored over the very sheets it was listed
    // with.
    const filterParams = new URLSearchParams();
    if (bobbinEnabled !== undefined) { filterParams.set('bobbin_enabled', bobbinEnabled); }
    disabledFilters.forEach((key) => filterParams.append('disabled_filters[]', key));
    Object.entries(selectedTypes).forEach(([key, types]) => {
      if (types.length === 0) { filterParams.append(`${key}[]`, ''); return; }
      types.forEach((type) => filterParams.append(`${key}[]`, type));
    });
    filterParams.set('area_ratio_percent', areaRatioPercent);
    // The quantity the server scores against -- see setCountParam. Shared into every per-line URL below.
    setCountParam(filterParams, count);

    const bestScoreUrl = (line) => {
      const query = new URLSearchParams(filterParams);
      query.set('manufacturing_line', line.identifier);
      query.set('sub_section_index', line.sub_section_index);
      return `/mcc/${window.user_quote_id}/manufacturing-line-best-score/${navSectionIndex}?${query.toString()}`;
    };

    // WHAT IS ALREADY KNOWN GOES ON SCREEN IN THIS RENDER, not a microtask later. Every cached line would
    // resolve almost immediately anyway, but "almost" is a frame with the scoring bar in it -- and after a
    // remount (leaving a section for Manage and coming back, or the guided setup wizard closing) EVERY line is
    // cached, so the user was shown "Scoring manufacturing lines…" for a listing that needed no scoring at all.
    // Seeding synchronously means a fully cached listing never shows the bar.
    const cachedScores = {};
    distinctLines.forEach((line) => {
      const url = bestScoreUrl(line);
      if (!hasCachedResponse(url)) { return; }

      const data = getCachedResponse(url);
      cachedScores[lineKey(line)] = data ? data.best_score : null;
    });

    let superseded = false;
    // Cancel a superseded batch's requests. An aborted signal makes an in-flight fetch reject AND makes a
    // still-QUEUED fetch reject immediately when the queue reaches it -- WITHOUT sending the request -- so a
    // rapid filter toggle does not pile N x 42 heavy solves on the server (each request runs a full solve).
    const controller = new AbortController();
    // How this pass gives the queue back the progress counters it is still holding -- see #releaseProgress,
    // which fills this in once they exist. Reached from both ways out (Cancel below, and the cleanup at the end
    // of this effect), so an abandoned pass releases them whichever one abandoned it.
    let releaseProgress = () => {};
    // Scoring a whole listing REPLACES what was on screen (it is a new listing), so the seed is all it keeps;
    // scoring one plan merges into the scores already there.
    if (scoresWholeListing) {
      setBestScoreByLine(cachedScores);
      setRetryingLines(new Set());
    } else if (Object.keys(cachedScores).length > 0) {
      setBestScoreByLine((previous) => ({ ...previous, ...cachedScores }));
    }
    sectionAnswerQueue.reset();

    // What Cancel does to this pass: abort the in-flight scores AND empty the queue, so the lines not yet
    // reached are never asked for -- each is a full solve, and the whole point of cancelling is to stop paying
    // for an answer nobody is waiting for. The scores already in hand stay on their cards.
    cancelScoringRef.current = () => {
      superseded = true;
      controller.abort();
      releaseProgress();
      sectionAnswerQueue.clear();
    };

    // WRITTEN THE MOMENT ONE MANUFACTURING LINE ANSWERS, not once the whole batch has: that is what lets each
    // card fill in its own score as it arrives, instead of the listing sitting blank behind a full-screen bar
    // and then appearing all at once. A superseded batch writes nothing.
    //
    // The key is recorded even when the score is null (an unmanufacturable manufacturing line), because the
    // card has to tell "answered, cannot be made" from "not answered yet" -- they look different: an em dash
    // against a still-loading placeholder.
    const recordScore = (key, best) => {
      if (superseded) { return; }

      setBestScoreByLine((previous) => ({ ...previous, [key]: best }));
      setRetryingLines((previous) => {
        if (!previous.has(key)) { return previous; }

        const remaining = new Set(previous);
        remaining.delete(key);
        return remaining;
      });
    };

    // Every manufacturing line whose score is not already in hand. One already computed for these exact inputs
    // (the wizard just scored it at this quantity -- the quantity is in the url) is taken straight from the
    // response cache: no request, no server-side solve, and it is settled below without ever being asked about.
    const outstanding = distinctLines.filter((line) => !hasCachedResponse(bestScoreUrl(line)));
    const lineByKey = new Map(distinctLines.map((line) => [lineKey(line), line]));

    // ONE unit of progress per manufacturing line, resolved as its own answer lands -- so the bar still counts
    // manufacturing lines, not requests, now that the whole section is asked for in one.
    const settle = new Map();
    const settled = distinctLines.map((line) => sectionAnswerQueue.enqueue(() => new Promise((resolve) => {
      settle.set(lineKey(line), resolve);
    })));
    const finish = (key) => { settle.get(key)?.(); settle.delete(key); };

    // GIVE BACK EVERY COUNTER THIS PASS IS STILL HOLDING, when the pass is abandoned.
    //
    // Those counters are promises that settle only when their own manufacturing line answers, and an abandoned
    // pass has answers that are never coming: the poll stops the moment its signal aborts, so nothing is left
    // to call finish for the rest. A promise that never settles is, to the queue, a request still in flight --
    // RequestQueueManager decrements only when one settles, and neither reset() nor clear() releases them.
    //
    // Left behind they accumulate, and they never come back: a handful of abandoned passes (a filter change, a
    // new quantity, a section switch, Cancel -- each abandons whatever had not answered yet) is enough to hold
    // every one of SECTION_ANSWER_CONCURRENCY's slots for the life of the page. After that no counter ever
    // starts, so none is ever resolved, and the section fills its cards in behind a scoring bar stuck at
    // nothing and a pass that never reads as finished.
    releaseProgress = () => {
      settle.forEach((resolve) => resolve());
      settle.clear();
    };

    distinctLines.forEach((line) => {
      if (outstanding.includes(line)) { return; }

      const data = getCachedResponse(bestScoreUrl(line));
      recordScore(lineKey(line), data ? data.best_score : null);
      finish(lineKey(line));
    });

    // A manufacturing line whose answer never arrives -- a worker killed mid-job, a poll that kept failing --
    // is recorded as answered rather than left on a placeholder for ever. This is the END of the road: the poll
    // has already retried and given up (see pollSectionBestScores), so it records what the old per-line
    // recovery recorded once its own attempts were exhausted.
    //
    // It does NOT mark the manufacturing line as still being retried. That flag existed because the old
    // recovery recorded a PROVISIONAL null straight away, to keep the rest of the listing from waiting out one
    // line's backoff, and the card had to know not to draw it as "cannot be manufactured". Asked together
    // there is no provisional null to disown: a manufacturing line that has not answered has nothing recorded
    // for it at all, so its card already reads unanswered.
    const markLost = (key) => {
      recordScore(key, null);
      finish(key);
    };

    pollSectionBestScores(
      `/mcc/${window.user_quote_id}/manufacturing-line-best-scores/${navSectionIndex}?${filterParams.toString()}`,
      outstanding.map(lineKey),
      {
        signal: controller.signal,
        onAnswer: (key, data) => {
          // Filed under the manufacturing line's OWN url, exactly where a single ask would have put it, so the
          // response cache and its IndexedDB half go on working as they did -- a remount, or coming back to
          // this section, finds these without asking again.
          const line = lineByKey.get(key);
          if (line) { setCachedResponse(bestScoreUrl(line), data); }

          recordScore(key, data ? data.best_score : null);
          finish(key);
        },
        onLost: markLost,
      },
    ).catch(() => { /* aborted: the cleanup below has already taken over */ });

    Promise.all(settled).then(() => {
      if (superseded) { return; }

      // This pass has finished -- what the blink waits for. Counted whether or not the numbers moved.
      setScoringCompletedCount((previous) => previous + 1);
      // Mark the listing fully scored only NOW, once the batch actually landed. Recording it at the START would
      // strand the listing scoreless if a pick interrupted the batch: the aborted run leaves bestScoreByLine {},
      // and the next run would skip on `ref === listing` and never score anything.
      if (scoresWholeListing) { fullyScoredListingRef.current = listing; }
    });

    return () => { superseded = true; controller.abort(); releaseProgress(); cancelScoringRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `listing` is the join point (a filter / bobbin /
    // area-ratio / section / COUNT change mints a new listing before this should re-run, and navSectionIndex is
    // stable per mounted instance), so only `listing` and `planScope` trigger this -- not the filter values
    // (the area-ratio cap among them) or the count the body reads: a count change re-lists above, and the new
    // listing object re-triggers this pass, which then scores against that same count via the cache key.
    //
    // `planScope` rather than `planOnScreen`: what this pass has to follow is the SCOPE -- which plans are on
    // screen and so expected to answer -- and that is the stage's, not the pick's. See its definition.
  }, [listing, planScope]);

  // A filter change re-lists, and the picked plan may no longer exist in the new listing -- so drop the
  // selection rather than show solutions for a plan that is no longer on screen. Clear the best scores in the
  // SAME pass, so the cards never show the previous listing's scores against the new listing for a render (the
  // best-score effect clears them too, but only after the render that first sees the new listing).
  //
  // Skip the very FIRST run: the picked plan is restored from the URL on mount (above), and this clear must not
  // wipe that restore -- only a later filter / section change should drop the selection.
  // Keyed off committedFilterKey (not the edited values), so the current listing's scores and stars stay put
  // while the user is still ticking -- they clear in the SAME pass the re-list fires, on Search.
  const selectionResetSkipRef = useRef(true);
  useEffect(() => {
    if (selectionResetSkipRef.current) { selectionResetSkipRef.current = false; return; }
    setPickedList(null);
    setBestScoreByLine({});
    setRetryingLines(new Set());
    // The chosen solution goes too: it was chosen out of a listing these filters have just replaced, so the
    // plan it was found on may no longer be offered at all.
    //
    // A SECTION change is different from a filter change, though: the new section may already be priced, and
    // its own selection has to come back. This component is NOT remounted when the page switches section --
    // FrankQuoteBuilderPage renders one section at a time and LayoutNavSectionList keys its child by the map
    // POSITION, which is always 0 for a one-element array -- so restoring only in the useState initializer left
    // an already-priced second section (a shirt box's Kapak) showing the solution listing instead.
    setSelectedSolutions(restoreSelectedSolutions(navSectionIndex));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- committedFilterKey is Search's trigger and
    // areaRatioPercent its own (blur-committed) one; a section switch resets at once and changes both, so the
    // reset stays in step with the re-list the same change fires.
  }, [navSectionIndex, committedFilterKey, areaRatioPercent]);

  // ANSWERED, not "answered with a score": a line whose request came back unmanufacturable is recorded as null,
  // so presence of the KEY is what separates "we know" from "still waiting". Reading the value cannot tell them
  // apart, and the card draws them differently. Declared HERE, above the early return below, so the flash effect
  // and the rendering read the same one definition of "answered".
  const lineScored = (line) => Object.prototype.hasOwnProperty.call(bestScoreByLine, lineKey(line));
  const listScored = (list) => list.manufacturing_lines.every(lineScored);

  // THE FLASH: dim the results for half a second as an asked-for search lands. Two searches can answer with the
  // very same manufacturing lines -- bobbin on and bobbin off are found over different material sheets, but the
  // same presses and cutters run both -- and a screen that settles back identical to the one before the press
  // reads as "the filter does nothing". This says the answer is fresh without claiming it is different.
  //
  // It fires when the section STOPS working (the listing is in and every line on screen has answered), and only
  // for a search someone pressed Search for: not the section's own first listing, and not a cancelled one, where
  // nothing arrived to show (both clear the ref).
  useEffect(() => {
    const awaited = awaitingSearchResultsRef.current;
    if (!awaited || isRefetchingListing) { return; }
    if (listingLandedCount <= awaited.landed) { return; }

    // WHAT IS LEFT TO WAIT FOR. A listing with nothing in it has no scoring pass, and neither has one that came
    // back as the very object already on screen -- the store answering a repeat of a search already made, which
    // is instant by design and the reason the section does not re-solve what it has just solved. Anything else
    // is a new listing whose lines are being scored right now, and blinking before that finishes would say
    // "here is your answer" over the previous search's numbers.
    const lists = (listing && listing.manufacturing_line_lists) || [];
    const nothingToScore = lists.length === 0 || listing === awaited.listing;
    if (!nothingToScore && scoringCompletedCount <= awaited.scored) { return; }

    awaitingSearchResultsRef.current = null;
    setResultsJustRefreshed(true);
  }, [isRefetchingListing, listing, listingLandedCount, scoringCompletedCount]);

  // THE TIMER IS THE FLAG'S OWN, keyed on nothing else. It used to be armed by the effect above, and so was
  // cleared by that effect's cleanup whenever a score or the listing changed -- which happens routinely inside
  // the flash's half second (a line answering late, a retry landing). The re-run then found the ref already
  // spent, armed nothing, and the results stayed dimmed for good. Here the only dependency is the flag itself,
  // so a raised flag always has exactly one timer to lower it.
  useEffect(() => {
    if (!resultsJustRefreshed) { return undefined; }

    const timer = setTimeout(() => setResultsJustRefreshed(false), RESULTS_BLINK_MILLISECONDS);
    return () => clearTimeout(timer);
  }, [resultsJustRefreshed]);

  // Mirror the picked plan into the address bar so a refresh / shared link reproduces it, AND tell the parent
  // what it now is. The parent gates the Solution stage on there being a picked plan, so it has to hear about
  // the plan going away as well as being chosen -- and a plan goes away in ways no click reports: a filter
  // change re-lists and drops it, and a plan restored from the address bar is dropped when the listing that
  // lands does not offer it. Told only on clicks, the page went on offering a Solution stage with nothing to
  // solve, which is what showed "Please select a Manufacturing Line to Continue" on a tab that should have
  // been closed.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    if (onLinePicked) { onLinePicked(pickedList); }
    if (pickedList) {
      writeSelectedManufacturingLineList(navSectionIndex, pickedList);
    } else {
      clearSelectedManufacturingLineList(navSectionIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onLinePicked is the parent's callback, reported
    // when the PICK changes; adding it as a dependency would re-report on every parent render instead.
  }, [pickedList, navSectionIndex]);

  // Mirror the SECTION'S FILTERS the same way, so whatever remounts this section -- closing the guided setup
  // wizard, a refresh, a shared link -- brings back the filters the listing on screen was found with, instead of
  // dropping to the server's defaults and re-listing the section from scratch.
  //
  // The EDITED values, not the committed ones: the address bar mirrors what is on screen, and a wizard closed
  // before Search should still reopen on the boxes the user had just ticked. The area-ratio cap is written only
  // while it differs from the page's default, so an ordinary visitor never carries an admin tuning knob around.
  useEffect(() => {
    if (navSectionIndex === undefined) { return; }
    writeSectionFilters(navSectionIndex, {
      machineTypes: selectedTypes,
      disabledFilters: disabledFilters,
      areaRatioPercent: areaRatioPercent === DEFAULT_AREA_RATIO_PERCENT ? null : areaRatioPercent,
    });
  }, [navSectionIndex, selectedTypes, disabledFilters, areaRatioPercent]);

  // A plan restored from the URL may name one the current listing does not hold (a shared link after the
  // catalogue or filters changed). Once the listing lands, drop such a stale selection so the page shows the
  // listing rather than an empty picked view.
  useEffect(() => {
    if (!listing || !pickedList) { return; }
    const lists = listing.manufacturing_line_lists || [];
    if (!lists.some((list) => list.identifier === pickedList)) { setPickedList(null); }
  }, [listing]);

  // Rank the solutions once their scores land. Each card fetches its OWN score (the legacy card does this
  // itself), so the scores arrive one at a time and the ranking can only settle after the last one -- which is
  // why the legacy re-runs sortDivs on a 200ms timer rather than once. Frank does the same, but STOPS: it polls
  // only while the solutions are on screen, and the interval is cleared when the picked plan goes away.
  useEffect(() => {
    if (!pickedList) { return undefined; }

    const timer = setInterval(() => sortSolutionsByScoreAndDrawStars(solutionsGridRef.current), 200);
    return () => clearInterval(timer);
  }, [pickedList, solutionGroups]);

  // THE PRICED PLAN, for the refresh path. When a solution is chosen but the (default-filtered) listing does not
  // hold its plan, fetch that plan by identifier from the UNFILTERED priced endpoint so the priced card can still
  // be drawn. This is the refresh case: the component remounts with no ticked machine-type filters, so the
  // listing comes back without a plan whose machines are off by default (a Flatbed UV press + a Digital cutter).
  // On a fresh in-session select the listing already holds the plan (the filters the viewer ticked to surface it
  // are still set), so nothing is fetched.
  //
  // WAITS FOR THE LISTING (does nothing while it is null): the listing loads async, so acting before it arrives
  // would read an empty listing, miss the plan, and fire a recovery fetch on every priced-view load -- even when
  // the listing is about to hold the plan. Once it has loaded, the plan-in-listing check is real.
  useEffect(() => {
    if (!window.frank_mode || navSectionIndex === undefined
        || !hasSelection(selectedSolutions) || !planOnScreen || !listing) {
      setPricedPlanData(null);
      return undefined;
    }
    const inListing = (listing.manufacturing_line_lists || []).some((list) => list.identifier === planOnScreen);
    if (inListing) { setPricedPlanData(null); return undefined; }

    const query = new URLSearchParams();
    query.set('manufacturing_line_list', planOnScreen);
    if (bobbinEnabled !== undefined) { query.set('bobbin_enabled', bobbinEnabled); }
    query.set('area_ratio_percent', areaRatioPercent);

    let superseded = false;
    fetch(`/mcc/${window.user_quote_id}/priced-manufacturing-line-list/${planSectionIndex}?${query.toString()}`)
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => { if (!superseded) { setPricedPlanData(data ? data.manufacturing_line_list : null); } })
      .catch(() => { if (!superseded) { setPricedPlanData(null); } });

    return () => { superseded = true; };
  }, [selectedSolutions, planOnScreen, listing, navSectionIndex, planSectionIndex, bobbinEnabled, areaRatioPercent]);

  // The normal /mcc page keeps this admin/v2 gate while the listing UX is still being productized. The guided
  // setup wizard, however, is the customer-facing surface (Frank only), so when this component is hosted inside
  // it (stageView set) the gate is relaxed and every Frank user can complete the flow.
  // NOTHING TO FOLLOW YET. The section that leads has chosen no manufacturing line, so this one has nothing to
  // inherit and nothing to show. One quiet line, not a panel: WHY there is nothing to choose here is said by the
  // marker on the Manufacturing Line tab (FrankQuoteBuilderPage), which costs no vertical space above the
  // answer. This only says what to do next.
  const renderInheritedPending = () => {
    const parentLabel = linkedParent && linkedParent.label;

    return (
      <div
        data-frank-inherited-pending
        style={{ textAlign: 'center', fontSize: '13px', color: 'var(--ink-faint, #838d99)', padding: '24px 8px' }}
      >
        {parentLabel
          ? t('quote_builder.frank_linked_section_pending', { section: parentLabel })
          : t('quote_builder.frank_linked_section_pending_unnamed')}
      </div>
    );
  };

  const canSee = stageView != null || window.current_user_admin || window.quote_builder_v2_enabled;

  // THE ASK FAILED, AND THAT IS AN ANSWER TOO. Said in the reader's own words, with the SERVER'S reason under
  // it for whoever can act on one: an administrator, or anyone running this in development, where the reason is
  // that nothing is listening on the manufacturing_cost_calculator queue and how to start one. A customer is
  // shown the sentence and the button, not the exception.
  //
  // Try again is the ask repeated, and nothing else: it bumps the same counter Search does (searchRunNumber is
  // a trigger of the listing fetch in its own right), so the very same filters are asked for again. It does not
  // go through runSearch, which also discards the picked plan and the chosen solution -- there is neither here,
  // and a failure is not the viewer changing their mind.
  const renderListingUnavailable = () => (
    <div
      data-listing-unavailable
      style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}
    >
      <div>{t('quote_builder.frank_manufacturing_lines_unavailable')}</div>
      {(window.current_user_admin || window.quote_builder_v2_enabled) && (
        <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--ink-faint, #8a94a3)' }}>
          {listingUnavailable}
        </div>
      )}
      <button
        type="button"
        data-retry-manufacturing-lines
        onClick={() => {
          setListingUnavailable(null);
          setSearchRunNumber((previous) => previous + 1);
        }}
        className="dct-button dct-button--secondary dct-button--small"
        style={{ marginTop: '16px' }}
      >
        {t('quote_builder.frank_try_again')}
      </button>
    </div>
  );

  if (!canSee) { return null; }

  // No listing is either "not yet" or "not coming", and the two must not draw the same: waiting draws nothing
  // (the section's own loading state covers it), a failure says so -- see #listingUnavailable.
  if (!listing) { return listingUnavailable ? renderListingUnavailable() : null; }

  // The reduction-toggle and area-ratio blocks are ADMIN tuning controls. They live in <DebugPanel>, which
  // portals into the admin debug popup -- but falls back to rendering INLINE when there is no popup (a
  // non-admin). On the normal page that never showed because canSee gated the whole component; in the wizard
  // (which relaxes that gate) they would render inline for customers, so gate them on the admin/v2 flags here.
  const adminDebugVisible = window.current_user_admin || window.quote_builder_v2_enabled;

  // What the page LISTS -- the ONLY thing it lists: every complete way to manufacture the section. The
  // sub_sections are the working behind these and are not listed: "Kapak" on its own makes no shirt box, it
  // makes a cover, and every one of its manufacturing lines already appears inside the lists that use it.
  const manufacturingLineLists = listing.manufacturing_line_lists || [];

  // THE PLANS THE PRICED VIEW REASONS ABOUT: the listing, plus the priced plan recovered for the refresh path
  // when the listing does not hold it (pricedPlanData). The priced card's lookup and the machine scale it is
  // drawn against read this, so a plan the default filters dropped is still drawn to scale. Null pricedPlanData
  // (nothing priced, or the listing already holds the plan) leaves this as just the listing.
  const effectiveManufacturingLineLists = pricedPlanData
    && !manufacturingLineLists.some((list) => list.identifier === pricedPlanData.identifier)
    ? [...manufacturingLineLists, pricedPlanData]
    : manufacturingLineLists;

  // The plans actually ON SCREEN: every one while the listing shows, just the one plan otherwise (picked, or
  // the plan a chosen solution is priced from). Scoring is scoped the same way (see the best-score effect), so
  // this is also the set that can be expected to answer -- asking whether the plans the viewer cannot see have
  // answered would leave that one card waiting forever. The wizard's line stage lists ALL plans (it scores them
  // all), so a restored pick does not narrow this to itself -- otherwise bestScoresReady would flip true after
  // one card and the rest would show blank scores.
  const listsOnScreen = (planOnScreen && stageView !== 'line')
    ? manufacturingLineLists.filter((list) => list.identifier === planOnScreen)
    : manufacturingLineLists;

  // Whether every plan on screen has answered. Individual cards no longer wait for this -- each shows its own
  // score as it lands -- but the two things that are a property of the SET still do: the best-first sort, and
  // the star ratings, which are a score's position in the range on screen and so cannot be known until the
  // range is complete.
  const bestScoresReady = listsOnScreen.length > 0 && listsOnScreen.every(listScored);

  // WHETHER THE SECTION IS STILL WORKING OUT THE ANSWER THE FILTERS DESCRIBE: the lines are being scored (they
  // are listed but not every one has answered). Only while there ARE lines to score -- with none (every
  // printing machine unticked, say) bestScoresReady never turns true, and a lock waiting on it would never
  // lift. A cancelled search is not in progress: nothing is coming, and the cards keep the scores that did land.
  const scoringInProgress = !searchCancelled && manufacturingLineLists.length > 0 && !bestScoresReady;

  // THE FILTER BLOCK IS LOCKED WHILE THE ANSWER IT DESCRIBES IS BEING COMPUTED -- the re-list AND the scoring
  // that follows it, which is by far the longer half.
  //
  // Ticking during the scoring changed the checkbox and re-ran the material-sheet finder AT ONCE, while the
  // lines and scores on screen were still the previous value's: the block then said "Bobbin" over a listing
  // found without it, and the debug popup's "Chosen material dimensions" showed sheets from whichever of the
  // two fetches happened to land -- the same section describing itself two ways. Locking the block until the
  // answer settles keeps the controls and the numbers below them the same reading.
  //
  // The lock is never a trap: while it holds, Cancel (below the block) stops the search and hands the filters
  // back, which is the only reason a viewer would want them mid-search.
  const filtersLocked = !searchCancelled && (isRefetchingListing || scoringInProgress);

  // Stop both passes, unlock, AND CLEAR WHAT THE STOPPED SEARCH HAD PRODUCED: a listing whose lines are half
  // scored is not an answer to anything -- it is a leftover of a question the viewer withdrew, and reading it as
  // a result (sorting it, starring it, picking from it) would be reading a partial one. So the lines and their
  // scores go; the section shows its filters and waits to be asked again.
  //
  // The listing OBJECT is kept (emptied) rather than nulled: it carries the machine-type filter rows this
  // section draws its checkboxes from, and nulling it would take the filters away with the results.
  const cancelSearch = () => {
    if (cancelListingRef.current) { cancelListingRef.current(); }
    if (cancelScoringRef.current) { cancelScoringRef.current(); }
    // Nothing arrived, so nothing blinks when the block unlocks.
    awaitingSearchResultsRef.current = null;
    setSearchCancelled(true);
    setListing((previous) => ({ ...(previous || {}), manufacturing_line_lists: [] }));
    setBestScoreByLine({});
    setRetryingLines(new Set());
  };

  // Ask for the edited filters. This is the ONLY thing that starts a find + score for a filter change -- see
  // the commit note on pendingFilterKey.
  const runSearch = () => {
    // A NEW START. The plan picked and the solution chosen came out of the listing this search is about to
    // replace -- and even a search that answers with the very same plans is the viewer asking the question
    // again, not confirming the answer they had. So the pick goes, and the chosen solution goes with it through
    // the same server-backed discard a changed pick uses (it un-prices the section, tells the linked sections,
    // and clears nothing locally until the server has actually done it).
    setPickedList(null);
    discardSelectionForChangedPlan();
    awaitingSearchResultsRef.current = {
      landed: listingLandedCount, scored: scoringCompletedCount, listing: listing
    };
    setSearchCancelled(false);
    setCommittedFilterKey(pendingFilterKey);
    setSearchRunNumber((previous) => previous + 1);
    // The section owns the material sheets (the bobbin is ITS question), so it re-finds them on the same press:
    // the sheets shown and the lines listed are then always the same bobbin's. Guarded on the DESCRIPTOR, not
    // on the function: a page with no bobbin question passes none at all (the legacy stacked view), but one
    // that draws the row and forgot #onSearch is miswired -- and would go back to showing chosen material
    // dimensions found for a different bobbin than the listing, silently.
    if (bobbin) { bobbin.onSearch(); }
  };

  // WHETHER THE LIST IS FINISHED, for the scroll-to-the-selection above -- which cannot ask for these two
  // itself, being declared before them. The Solution tab draws a skeleton while the solve is out
  // (renderSolutions), and the listing SORTS once every score has landed, so a glide before either would land
  // where the marked card used to be.
  listSettledRef.current = stageView === 'solution' ? !solutionsLoading : bestScoresReady;

  // A LIST's best = the SUM of its lines' bests (parts are made independently on their own lines). null when any
  // of its lines is unmanufacturable (no solution) -- that list cannot make the section, so it sorts last.
  const listBestScore = (list) => {
    const perLine = list.manufacturing_lines.map((line) => bestScoreByLine[lineKey(line)]);
    if (perLine.some((score) => score == null)) { return null; }

    return perLine.reduce((sum, score) => sum + score, 0);
  };

  // The range of the lists' best scores on screen -- the star ratings normalize against it (best = 5, worst =
  // 1). Unmanufacturable plans (null) are excluded from the range and get no stars.
  const listBestScores = bestScoresReady
    ? listsOnScreen.map(listBestScore).filter((score) => score != null)
    : [];
  const minListScore = listBestScores.length ? Math.min(...listBestScores) : 0;
  const maxListScore = listBestScores.length ? Math.max(...listBestScores) : 0;
  // The filters are the SECTION's: one set, applied to every sub_section the lists are built from.
  const machineTypeFilters = listing.machine_type_filters || [];
  const filters = listing.filters || [];

  // Largest machine sheet dimension across every manufacturing line the page draws -- the common scale that
  // makes the per-machine rectangles comparable to each other (a bigger machine renders bigger), including
  // across the two lines stacked in one list. The same scale the legacy listing computes.
  const allManufacturingLines = effectiveManufacturingLineLists.flatMap((list) => list.manufacturing_lines);
  const maxMachineDimensionMm = Math.max(1, ...allManufacturingLines.flatMap((manufacturingLine) => [
    manufacturingLine.sheet_width_mm, manufacturingLine.sheet_height_mm,
    manufacturingLine.printing_sheet_width_mm, manufacturingLine.printing_sheet_height_mm
  ].map((value) => parseFloat(value) || 0)));

  // The SECTION's filter rows, drawn as ONE aligned card so the bobbin question and the two machine-type rows
  // read as a single control:
  //     Malzeme:                    [x] Bobin
  //     Baskı Makineleri:      [x] Offset  [ ] Dijital ...
  //     Kalıplı kesim makineleri:  [ ] Dijital  [x] Otomatik ...
  // The ticks are instant and local, and nothing is found or scored until Search (the button below the rows,
  // in the same card). While THAT runs the block dims and its inputs disable (filtersLocked) -- see the wrapper
  // below -- so the viewer sets the boxes they want, asks once, and cannot change them again until the listing
  // AND its scoring have settled. The bobbin row is Frank's: the legacy page draws its own in
  // ChooseLayoutDimensions, and this component is /mcc only.
  const headingStyle = { fontSize: '13px', color: 'var(--ink, #10141a)', fontWeight: 600, whiteSpace: 'nowrap' };
  const optionsRowStyle = { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '16px' };
  const optionLabelStyle = {
    fontSize: '13px', color: 'var(--ink-soft, #4e5865)', fontWeight: 500,
    display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer'
  };
  const checkboxStyle = { accentColor: 'var(--brand, #eb5424)', width: '15px', height: '15px', margin: 0, cursor: 'pointer' };

  const renderSectionFilters = () => {
    const bobbinRowShown = bobbin && bobbin.shown;

    // Drawn even for a section with no rows to tick -- a bobbin question it is not asked and no machine-type
    // filters -- because the card is not only the rows: Search is in it, and asking the section to be worked
    // out again is worth a button whether or not there is anything to narrow it by.
    return (
      <div
        style={{
          display: 'inline-grid', gridTemplateColumns: 'max-content 1fr', gap: '10px 20px', alignItems: 'center',
          padding: '12px 18px', textAlign: 'left',
          background: 'var(--surface, #ffffff)', border: '1px solid var(--rule, #e3e7ec)',
          borderRadius: 'var(--dct-radius, 8px)', boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)'
        }}
      >
        {bobbinRowShown && (
          <React.Fragment>
            <span style={headingStyle}>{t('quote_builder.material')}:</span>
            <div data-section-bobbin-filter style={optionsRowStyle}>
              <label style={{ ...optionLabelStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={bobbin.checked}
                  disabled={bobbin.forced || filtersLocked}
                  data-bobbin-checkbox
                  onChange={(event) => bobbin.onChange(event.target.checked)}
                  style={{ ...checkboxStyle, cursor: bobbin.forced ? 'default' : 'pointer' }}
                />
                {t('quote_builder.bobbin_label')}
              </label>
            </div>
          </React.Fragment>
        )}

        {machineTypeFilters.map((filter) => {
          const labels = MACHINE_TYPE_FILTER_LABELS[filter.key];
          if (!labels) { return null; }

          const currentlySelected = filter.options.filter((option) => option.selected).map((option) => option.value);
          // The EDITED selection (the ticks), not the server echo -- so a tick shows at once before the search
          // window, before the re-list that will confirm it. Absent means "not spoken yet" -> the server's default.
          const editedSelected = selectedTypes[filter.key] || currentlySelected;

          return (
            <React.Fragment key={filter.key}>
              <span style={headingStyle}>{t(labels.headingKey)}:</span>
              <div data-machine-type-filter={filter.key} style={optionsRowStyle}>
                {filter.options.map((option) => (
                  <label key={option.value} style={optionLabelStyle}>
                    <input
                      type="checkbox"
                      checked={editedSelected.includes(option.value)}
                      disabled={filtersLocked}
                      data-machine-type={option.value}
                      onChange={() => toggleMachineType(filter.key, option.value, currentlySelected)}
                      style={checkboxStyle}
                    />
                    {t(labels.optionKey(option.value))}
                  </label>
                ))}
              </div>
            </React.Fragment>
          );
        })}

        {/* ASK. Nothing above is found or scored until this is pressed -- until then the ticks are an edit and
            nothing more. It spans both columns under the rows it submits, in the same card, so what is being
            asked for and the asking are one control. */}
        <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', marginTop: '2px' }}>
          <button
            type="button"
            data-search-manufacturing-lines
            onClick={runSearch}
            disabled={filtersLocked}
            className="dct-button dct-button--primary dct-button--small"
          >
            {t('nav.search')}
          </button>
        </div>
      </div>
    );
  };

  // The SECTION's reduction toggles. Each reduces every sub_section's manufacturing lines; unticking one
  // re-fetches and the manufacturing lines it was hiding reappear.
  const renderFilterToggles = () => (
    <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '16px', marginTop: '8px' }}>
      {filters.map((filter) => (
        <label key={filter.key} style={optionLabelStyle}>
          <input
            type="checkbox"
            // The EDITED value (disabledFilters), not the server echo, so the tick shows before the search runs.
            checked={!disabledFilters.includes(filter.key)}
            disabled={filtersLocked}
            data-filter-key={filter.key}
            onChange={() => toggleFilter(filter.key)}
            style={checkboxStyle}
          />
          {t(`quote_builder.${FILTER_LABEL_KEYS[filter.key]}`)}
        </label>
      ))}
    </div>
  );

  // The always-on area-ratio reduction's cap, as an adjustable PERCENT -- the Debug knob for tuning how much
  // bigger than the sheet a printing bed may be before the line is dropped. It reads as the rule itself: "Drop a
  // printing bed more than [20] % larger in area than the sheet." Committed on blur / Enter (commitAreaRatio),
  // which re-lists and re-scores against the new cap.
  const renderAreaRatioControl = () => (
    <label style={{ fontSize: '12px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
      Drop a printing bed more than
      <input
        type="text"
        inputMode="numeric"
        data-area-ratio-percent
        value={areaRatioInput}
        onChange={(event) => setAreaRatioInput(event.target.value)}
        onBlur={commitAreaRatio}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            commitAreaRatio();
          }
        }}
        style={{
          width: '56px', padding: '2px 6px', fontSize: '12px', textAlign: 'right',
          border: '1px solid #d1d5db', borderRadius: '4px'
        }}
      />
      % larger in area than the sheet
    </label>
  );

  // A machine is drawn ONCE per list card. The lines of a list routinely share a machine (one press running
  // both the Cover and the Tray), and drawing it per line reads as though the plan needs two of them. So walk
  // the lines in order and, for a machine already drawn above, leave its column EMPTY rather than repeat it:
  // the empty slot IS the signal -- an empty printing space on the lower line means it prints on the machine
  // shown above it.
  //
  // ONE rule for every part_type, never an `if carton`: an offset pair's press and die-cutter and a flexo
  // line's single combined machine are all just machines. One Set holds them all -- every machine is a
  // UserUnitPriceAttribute row (keyed printing_machine / die_cutting_machine / printing_and_die_cutting_machine),
  // so the ids are unique across the kinds and a press can never collide with a die-cutter.
  const columnVisibilityFor = (manufacturingLines) => {
    // machineId -> the INDEX of the manufacturing line that drew it. Which one drew it matters, not merely
    // that one did: a manufacturing line with no box left is badged onto the line that draws its machines, and
    // only that line can honestly say "this part runs on these machines".
    const drawnBy = new Map();

    return manufacturingLines.map((manufacturingLine, index) => {
      const machineIds = [
        manufacturingLine.printing_machine_id,
        manufacturingLine.die_cutting_machine_id,
        manufacturingLine.printing_and_die_cutting_machine_id
      ];
      const alreadyDrawn = (machineId) => machineId != null && drawnBy.has(machineId);
      const ownMachineIds = machineIds.filter((machineId) => machineId != null);

      // The manufacturing lines that drew this one's machines -- undefined for a machine nobody drew yet.
      const owners = new Set(ownMachineIds.map((machineId) => drawnBy.get(machineId)));
      // Droppable ONLY when every machine came from ONE earlier manufacturing line. That line's machines ARE
      // this one's, so it can carry the badge. Machines drawn by two DIFFERENT lines have no honest target
      // (the part runs on a pair no single row shows), and a line nobody drew from still has a box of its
      // own -- both keep their row. A machine-less line has no owners at all, so it keeps its row too.
      const drawnByOneManufacturingLine = owners.size === 1 && !owners.has(undefined);

      const visibility = {
        hidePrintingColumn: alreadyDrawn(manufacturingLine.printing_machine_id),
        hideDieCuttingColumn: alreadyDrawn(manufacturingLine.die_cutting_machine_id),
        hideCombinedColumn: alreadyDrawn(manufacturingLine.printing_and_die_cutting_machine_id),
        drawsNothing: drawnByOneManufacturingLine,
        // The manufacturing line whose badges this one joins, or null when it keeps its own row.
        badgeOntoIndex: drawnByOneManufacturingLine ? [...owners][0] : null
      };

      // FIRST drawer wins: it is the row the machine is actually drawn on.
      machineIds.forEach((machineId) => {
        if (machineId != null && !drawnBy.has(machineId)) { drawnBy.set(machineId, index); }
      });
      return visibility;
    });
  };

  // One ManufacturingLineList as a single card: a complete way to manufacture the section. A list covering two
  // sub_sections stacks BOTH its manufacturing lines in the one card -- so a card is the familiar machine card,
  // with more machines in it. Each line is badged with the sub_section it makes, since a card holding two
  // identical-looking machine pairs is otherwise unreadable.
  //
  // The card decides which columns each manufacturing line draws, because it is the only place that knows the
  // list's OTHER manufacturing lines -- a ManufacturingLineCard renders the one it is handed.
  //
  // A manufacturing line whose machines were ALL drawn by ONE line above has no box left, so it is not given a
  // row of its own: an empty row under a lone badge reads as unfinished. Its part is badged onto THAT line --
  // the one whose machines are its machines -- so the card reads "Kapak and Kutu, on this pair".
  // alone: the plan is the only card on screen (the picked view), so it is not one of a 4-across grid and must
  // not be squeezed to a quarter width.
  // readOnly: the priced view, where the card names the plan the prices are about rather than offering a pick.
  // Choosing again there goes through the "choose a different solution" button, which de-selects server-side
  // too -- a card that silently un-picked the plan under a priced section would leave the two disagreeing.
  const renderManufacturingLineList = (list, { alone = false, readOnly = false } = {}) => {
    const columnVisibility = columnVisibilityFor(list.manufacturing_lines);
    const drawnManufacturingLines = list.manufacturing_lines
      .map((manufacturingLine, index) => ({ manufacturingLine, index }))
      .filter(({ index }) => !columnVisibility[index].drawsNothing);

    // This manufacturing line's own part, plus the parts of any whose machines IT draws. Badging onto the
    // owner rather than the first row is what keeps the card honest once a list has more than two lines: with
    // three, the machines of the third can belong to the second, and saying it runs on the first's pair would
    // name machines it never touches.
    const sectionLabelsFor = (index) => [
      list.manufacturing_lines[index].sub_section_label,
      ...list.manufacturing_lines
        .filter((_manufacturingLine, otherIndex) => columnVisibility[otherIndex].badgeOntoIndex === index)
        .map((manufacturingLine) => manufacturingLine.sub_section_label)
    ];

    const picked = pickedList === list.identifier;
    // On the Manufacturing Line tab, the SELECTED line is the pick, or -- on a reopened priced section that
    // never held a pick -- the plan its chosen solution is priced from. Either way that line is highlighted and
    // the rest are dimmed, so "this is the selected manufacturing line" reads at a glance.
    const isSelectedLine = list.identifier === (pickedList || pricedListIdentifier);
    // The priced view's card IS the plan in question, so it carries the picked highlight whether or not the
    // page still holds a pick -- a link straight into a priced section never had one.
    const highlighted = picked || readOnly || (stageView === 'line' && isSelectedLine);
    // The plan's best (cheapest) way to make the whole section -- the sum of its lines' bests. Shown once every
    // line's score has landed; '—' when the plan is unmanufacturable (a line with no solution). Its star rating
    // is the score's position in the range on screen: best = 5 stars, worst = 1 (linear min-max, matching the
    // solution cards).
    // A plan one of whose lines is still being asked again reads as UNANSWERED, whatever is recorded for it:
    // the record only exists so the rest of the listing can finish, and drawing it would show this plan's own
    // card the em dash that means "cannot be manufactured" -- for a line we have not given up on yet.
    const retrying = list.manufacturing_lines.some((line) => retryingLines.has(lineKey(line)));
    const scored = listScored(list) && !retrying;
    const listBest = scored ? listBestScore(list) : null;
    const rating = bestScoresReady ? starRatingForScore(listBest, minListScore, maxListScore) : null;

    // Picking toggles the plan. On a USER pick (not a restore from the URL) tell the parent, so the page's
    // Manufacturing Line tab can auto-advance to the Solution tab.
    //
    // CHANGING THE MANUFACTURING LINE INVALIDATES WHAT WAS CHOSEN ON THE OLD ONE. A solution is one way of
    // making this section with ONE manufacturing line, and the price is what THAT solution costs -- so once
    // the line changes, both describe a plan the user is no longer making. Left in place they came back on the
    // next refresh and dragged the pick with them (the priced plan is restored over the address bar), which is
    // how a freshly picked line could turn back into the old one.
    const pickThisPlan = () => {
      const next = picked ? null : list.identifier;
      if (next && next !== pricedListIdentifier) { discardSelectionForChangedPlan(); }
      // The parent hears about this through the pick's own effect above, which reports every change rather
      // than only the clicked ones.
      setPickedList(next);
    };
    // Fade the un-selected plans once one is selected, so the selected manufacturing line stands out on the
    // page's Manufacturing Line tab.
    const dimmed = stageView === 'line' && Boolean(pickedList || pricedListIdentifier) && !isSelectedLine;

    return (
      <div
        key={list.identifier}
        className={styles.combinations_grid_item_cont}
        data-manufacturing-line-list={list.identifier}
        // The card a Manufacturing Line tab scrolls to when it opens -- the same marker the solution cards
        // carry (LayoutNavSectionCombinationListList), so #scrollToSelectedCard has one selector for both.
        // Only on the LISTING: the priced view draws this card too, alone and read-only, and there is nothing
        // to scroll to when the page holds a single card.
        data-selected-card={stageView === 'line' && isSelectedLine && !alone ? true : undefined}
        // Picking a plan asks "how would THIS plan make the section?" -- the solve, which the listing
        // deliberately does not do. Clicking the picked one again goes back to the listing.
        role={readOnly ? undefined : 'button'}
        tabIndex={readOnly ? undefined : 0}
        onClick={readOnly ? undefined : pickThisPlan}
        onKeyDown={readOnly ? undefined : (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            pickThisPlan();
          }
        }}
        style={{
          display: 'flex', flexDirection: 'column', boxSizing: 'border-box',
          flex: alone ? '0 0 auto' : `0 0 ${inWizard ? WIZARD_CARD_BASIS : PAGE_CARD_BASIS}`,
          maxWidth: alone ? '360px' : (inWizard ? WIZARD_CARD_BASIS : PAGE_CARD_BASIS),
          margin: '8px', padding: '12px 8px', cursor: readOnly ? 'default' : 'pointer',
          border: highlighted ? '2px solid var(--brand, #eb5424)' : '1px solid #c4ccd6',
          borderRadius: '6px',
          opacity: dimmed ? 0.8 : 1, transition: 'opacity 120ms ease'
        }}
      >
        {/* THE SCORE SLOT, always the same height so a card never jumps when its number lands. Each row fills
            in as soon as what it needs is known: the VALUE the moment this plan's own lines have answered, the
            STARS only once every plan on screen has, because a rating is a position in that range. A plan that
            answered "cannot be made" shows an em dash -- it is answered, not still loading.
            DROPPED on the read-only priced card when it has no score: after a refresh that card is drawn from the
            plan recovered by pricedPlanData, which the best-score pass (it runs over the listing, and the listing
            no longer holds the plan) never scores -- so the slot would sit as a permanent skeleton. The card is
            about WHICH plan was priced, and the machines below carry that, so it simply drops the score row. */}
        {(!readOnly || scored) && (
        <div
          data-best-score={listBest == null ? '' : listBest}
          style={{ textAlign: 'center', marginBottom: '6px' }}
        >
          <div style={{
            marginBottom: '2px', minHeight: '16px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}
          >
            {bestScoresReady && !retrying
              ? (rating != null ? <Stars rating={rating} /> : null)
              : <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonStars}`} />}
          </div>
          {scored ? (
            <div style={{ fontSize: '13px', fontWeight: 'bold', color: listBest == null ? '#c00' : '#2e7d32' }}>
              {listBest == null ? '—' : `Skor: ${listBest.toLocaleString()}`}
            </div>
          ) : (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <div className={`${skeletonStyles.skeleton} ${skeletonStyles.scoreSkeletonValue}`} />
            </div>
          )}
        </div>
        )}
        {drawnManufacturingLines.map(({ manufacturingLine, index }) => (
          <ManufacturingLineCard
            key={`${manufacturingLine.identifier}-${index}`}
            manufacturingLine={manufacturingLine}
            maxMachineDimensionMm={maxMachineDimensionMm}
            sectionLabels={sectionLabelsFor(index)}
            bare
            hidePricedSolutionCount
            hidePrintingColumn={columnVisibility[index].hidePrintingColumn}
            hideDieCuttingColumn={columnVisibility[index].hideDieCuttingColumn}
            hideCombinedColumn={columnVisibility[index].hideCombinedColumn}
            widthStyle={{ width: '100%', maxWidth: 'none', margin: 0 }}
          />
        ))}
        {/* An explicit "Select" button, so it is obvious the whole card is a choice -- the card is clickable, but
            that affordance is easy to miss. It does the same pickThisPlan as clicking the card; stopPropagation
            keeps the card's own onClick from ALSO firing (which would toggle the pick right back). Frank surfaces
            only (they set stageView, so t() resolves); not on the read-only priced card or the picked-plan
            header (both `alone`). */}
        {stageView && !readOnly && !alone && (
          <button
            type="button"
            data-frank-select-manufacturing-line={list.identifier}
            className={`dct-button dct-button--small dct-button--block ${isSelectedLine ? 'dct-button--primary' : 'dct-button--secondary'}`}
            // marginTop:auto, not a fixed gap: the cards in a row are stretched to a common height (see the
            // listing row), so the button is pushed to the bottom of whatever height this card ended up with and
            // every Select in the row lines up.
            style={{ marginTop: 'auto', paddingTop: '10px' }}
            onClick={(event) => { event.stopPropagation(); pickThisPlan(); }}
          >
            {isSelectedLine ? t('quote_builder.frank_selected') : t('quote_builder.frank_select')}
          </button>
        )}
        {/* Once THIS manufacturing line is the selected one, an explicit "Next" button walks the page's section
            tabs on to the Solution stage -- so the user is not left hunting for the numbered stage tab above.
            onGoToNextStage is the page's advance-one-stage callback; the wizard does not pass it (it has its own
            Next in the modal footer), so this button never appears there. stopPropagation keeps the card's own
            onClick from toggling the pick back off. */}
        {stageView === 'line' && isSelectedLine && !readOnly && !alone && onGoToNextStage && (
          <button
            type="button"
            data-frank-next-stage
            className="dct-button dct-button--small dct-button--block dct-button--primary"
            style={{ marginTop: '8px' }}
            onClick={(event) => { event.stopPropagation(); onGoToNextStage(); }}
          >
            {t('quote_builder.frank_next')}
          </button>
        )}
      </div>
    );
  };

  // The control that goes back to the listing. The picked plan itself stays on screen as its own card above the
  // solutions, so this is only the way back.
  const renderGoBack = () => (
    <button
      type="button"
      data-frank-go-back
      onClick={() => setPickedList(null)}
      className="btn simple-btn"
      style={{ marginBottom: '12px' }}
    >
      Go Back
    </button>
  );

  // The picked plan's SOLUTION GROUPS -- one CARD per group, each holding one solution per part.
  //
  // A plan makes the WHOLE section, and may need two manufacturing lines to do it ("Kapak on the KBA + BOBST,
  // Kutu on the 70x100 Pedal"). Those are two halves of ONE answer, so one card carries both: the legacy's
  // LayoutNavSectionCombinationListList takes a `list` of solutions and draws them all inside a single grid
  // item, which is exactly what a group is (see #buildSolutionGroups for how they are combined).
  //
  // A plan with NO groups is a real answer, not a blank: its machines can be listed for the section's sheets
  // and still fold nothing both beds can run -- and if ONE part cannot be made, the plan cannot make the
  // section at all. So it says so, rather than showing an empty page that reads as broken.
  const renderSolutions = () => {
    // NOTHING TO SOLVE YET. The Solution tab can be reached (via its numbered stage tab) before a manufacturing
    // line has been picked -- there is no plan to solve, so instead of an empty or "no solutions" grid it prompts
    // the user back to the Manufacturing Line stage. A priced section always has its plan (selectedSolutions is
    // restored on mount), so this is only the truly-empty case. Page tabs only: the wizard does not pass
    // onGoToManufacturingLine (its Next is disabled until a line is picked, so it never lands here).
    if (onGoToManufacturingLine && !pickedList && !hasSelection(selectedSolutions)) {
      return (
        <div data-frank-no-line-selected style={{ textAlign: 'center', padding: '32px 12px' }}>
          <div style={{ fontSize: '14px', color: 'var(--ink-soft, #4e5865)', marginBottom: '16px' }}>
            {t('quote_builder.frank_select_manufacturing_line_prompt')}
          </div>
          <button
            type="button"
            data-frank-go-to-manufacturing-line
            className="dct-button dct-button--small dct-button--primary"
            onClick={onGoToManufacturingLine}
          >
            {t('quote_builder.frank_go_to_manufacturing_line')}
          </button>
        </div>
      );
    }

    // THE SOLVE, in place: the picked plan's card stays on screen above this and the grid area shows the work
    // instead of a full-screen card over the whole page. It draws a fixed few placeholders and an INDETERMINATE
    // bar -- the solve is one request, so neither how many cards are coming nor how far along it is can be
    // known (see SolutionCardsSkeleton).
    if (solutionsLoading) { return <SolutionCardsSkeleton />; }

    // In a stage view (the wizard's Solution stage or the page's Solution tab) a click just MARKS the card --
    // the marked one is outlined, and it is committed later (wizard Done / page Pricing tab). onSolutionPicked
    // lets the page tabs auto-advance (no-op in the wizard, which has its own Next). On the legacy stacked view
    // (no stageView) a click chooses the solution and prices the section straight away (setSelectedSolutions).
    const useMark = stageView === 'solution';
    // Clicking the marked card again UNMARKS it (see markSolution), so what is reported is the card that ends
    // up marked -- null when the click cleared it. The page's Pricing tab opens on the strength of this, and a
    // section with nothing marked has no price to show.
    const onCardClick = useMark
      ? (group) => {
        const unmarking = groupUuids(markedSolution) === groupUuids(group);
        markSolution(group);
        if (onSolutionPicked) { onSolutionPicked(unmarking ? null : group); }
      }
      : setSelectedSolutions;
    // FALLBACK for a PRICED section with nothing solved to show. Rather than tell the user "no solutions" for a
    // section they clearly priced, show the priced solution itself. Only when nothing new is being marked, so an
    // in-session choice always wins.
    //
    // It stands in for MORE THAN ONE cause, which is why the heading below does not speak for the plan: the
    // solve genuinely produced nothing (a part that cannot be made on its machine), or no solve ran at all --
    // there is no plan picked, which a priced section reaches by simply clicking its selected manufacturing line
    // card again (pickThisPlan toggles the pick off). Called "Solutions (1) -- how this plan makes the section",
    // as it was, the second case claimed the plan makes the section exactly one way, on the strength of a solve
    // that never happened.
    const showPricedFallback = useMark && solvedGroups.length === 0
      && !hasSelection(markedSolution) && hasSelection(selectedSolutions);
    const groups = showPricedFallback ? [selectedSolutions] : solvedGroups;
    // WHAT THE GRID IS, and only what is actually known: the plan's answers when they were solved for, and
    // otherwise the one thing the fallback really has -- the solution this section is priced on. The count is
    // dropped with the claim; "(1)" was never a fact about the plan there.
    const heading = showPricedFallback
      ? t('quote_builder.frank_priced_solution_heading')
      : t('quote_builder.frank_solutions_heading', { count: groups.length });
    // The SELECTED solution to emphasise on the Solution tab -- `chosenSolution`, the SAME value reported to the
    // wizard, so what the card draws as chosen and what the wizard's Next acts on are one thing. Its card is
    // outlined and the rest are dimmed, so the chosen solution stands out (mirrors the Manufacturing Line tab).
    const markedUuids = useMark ? groupUuids(chosenSolution) : '';
    // i18n on the customer-facing stage views (wizard + page tabs); the legacy stacked view keeps its exact
    // English, which its jest suite asserts on (t() returns the key there).
    const translated = stageView != null;

    // The grid's marker names the grid AND the plan it is for. It is written as an empty string rather than the
    // bare `pickedList`, because React omits an attribute set to null altogether -- which made the grid
    // unfindable in the one state most worth inspecting: no plan picked, which is when the fallback above is
    // what is on screen. That has already misled two measurements (a browser script and an example in
    // ManufacturingLineListsStageSolve), both reading "no solutions grid" off a grid that was plainly there.
    return (
      <div data-frank-solutions={pickedList || ''}>
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '8px', textAlign: 'center' }}>
          {translated
            ? heading
            : <>Solutions ({groups.length}) — how this plan makes the section</>}
        </div>

        {groups.length === 0 ? (
          <div style={{ textAlign: 'center', fontSize: '12px', color: '#c00', padding: '8px' }}>
            {translated
              ? t('quote_builder.frank_plan_no_solutions')
              : <>This plan produces no solutions: one of its parts cannot be made on the machine it is assigned to.</>}
          </div>
        ) : null}

        {/* THE LEGACY'S OWN SOLUTION VIEWS, reused verbatim -- the same reuse ManufacturingLineCard already
            gets, and the same distinction: legacy COMPONENTS are fine, legacy SERVICES are not
            (frank.contract, THE FRONT DOOR). A solution reads identically on both pages BY CONSTRUCTION rather
            than by a Frank copy that drifts from it.

            It works because Frank's Frank::DieCuttingSolution#to_hash is deliberately the legacy's shape, so
            the props map straight onto it: diecutting_result, nav_section_id, count, and
            material_layout_dimension as layout_dimension. That was the point of keeping the hash identical. */}
        {/* justifyContent overrides the shared .combinations_grid's left alignment for the same reason as the
            listing above -- a lone solution card in the corner reads as a broken page. Set here rather than in
            the shared stylesheet: that class is the legacy page's grid too, and this is a Frank decision. */}
        <div
          className={styles.combinations_grid}
          ref={solutionsGridRef}
          style={{ justifyContent: groups.length < cardsPerRow ? 'center' : 'flex-start' }}
        >
          {groups.map((group) => (
            <LayoutNavSectionCombinationListList
              key={groupUuids(group)}
              list={group}
              count={group[0].count}
              setSelectedList={onCardClick}
              selected={markedUuids !== '' && groupUuids(group) === markedUuids}
              dimmed={markedUuids !== '' && groupUuids(group) !== markedUuids}
              columnBasis={inWizard ? WIZARD_CARD_BASIS : null}
              // An explicit "Select" button under each solution card, on the Frank surfaces (the wizard's
              // Solution stage + the page's Solution tab), so the card's clickability is obvious.
              selectButton={translated}
              selectLabel={t('quote_builder.frank_select')}
              selectedLabel={t('quote_builder.frank_selected')}
              // Under the SELECTED solution card, a "Next" button walks the page's section tabs on to Pricing
              // (which commits and prices the marked solution). The wizard does not pass onGoToNextStage -- it
              // has its own Next -- so this stays off the wizard's cards.
              nextButton={translated && !inWizard && Boolean(onGoToNextStage)}
              nextLabel={t('quote_builder.frank_next')}
              onNext={onGoToNextStage}
            />
          ))}
        </div>
      </div>
    );
  };

  // THE LISTING: every complete way to manufacture the section. Shown only while nothing is picked. Sorted
  // best-first once every line's score has landed (listing order until then -- the progress overlay covers it).
  const renderListing = () => {
    // In the WIZARD / a section Manufacturing Line tab, hold the cards until every line has answered and show the
    // scoring progress inline instead: a page of blank-scored cards reads as broken, and the page's full-screen
    // ListingProgressOverlay would sit behind the modal. Only while there ARE lines to score -- with none
    // (e.g. every printing machine unticked) scoringInProgress is false, which would leave the progress bar
    // stuck; instead fall through to the empty listing below. The normal page is unchanged (stageView undefined).
    if (stageView === 'line' && scoringInProgress) { return <WizardScoringProgress onCancel={cancelSearch} />; }

    // A CANCELLED SEARCH HAS NO RESULTS TO SHOW, and it did not fail to find any either -- it was withdrawn. So
    // the section shows its filters and nothing else, and says nothing: the empty message below would report a
    // finding this search never made.
    if (searchCancelled) { return null; }

    // Nothing matches the current filters (e.g. no printing machine selected). Say so rather than showing an
    // empty "(0)" list.
    if (stageView && manufacturingLineLists.length === 0) {
      return (
        <div style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}>
          {t('quote_builder.frank_no_manufacturing_lines')}
        </div>
      );
    }

    const sortedLists = bestScoresReady
      ? [...manufacturingLineLists].sort((a, b) => {
        const scoreA = listBestScore(a);
        const scoreB = listBestScore(b);
        if (scoreA == null) { return scoreB == null ? 0 : 1; } // unmanufacturable plans sort last
        if (scoreB == null) { return -1; }
        return scoreA - scoreB; // lower score is better -- it is a cost
      })
      : manufacturingLineLists;

    return (
      // The results BLINK as an asked-for search lands (resultsJustRefreshed), so a search that answers the same
      // as the last one still shows that it answered. See the blink effects above and the animation itself in
      // frank/_style.module.scss.
      <div
        data-listing-results
        data-listing-results-blinking={resultsJustRefreshed ? 'true' : 'false'}
        style={{ opacity: resultsJustRefreshed ? RESULTS_BLINK_OPACITY : 1 }}
      >
        {/* i18n only on the customer-facing WIZARD surface (see the note in renderSolutions); the admin/normal
            page keeps its exact English, which its test suite asserts on. */}
        <div style={{ fontSize: '12px', color: '#888', marginBottom: '6px', textAlign: 'center' }}>
          {stageView
            ? t('quote_builder.frank_manufacturing_line_lists_heading', {
              count: manufacturingLineLists.length,
              sorted: bestScoresReady ? t('quote_builder.frank_sorted_by_best_score') : ''
            })
            : (
              <>
                Manufacturing Line Lists ({manufacturingLineLists.length}) — every complete way to manufacture this
                section{bestScoresReady ? ', sorted by best score' : ''}. Click one to see its solutions.
              </>
            )}
        </div>
        {/* The listing's progress while its manufacturing lines are scored: the modal, blocking popup over the
            whole page, sweeping until the first line answers and counting percentage after that. It clears
            itself once every line has answered. Gated on !stageView, so this is the LEGACY STACKED view only --
            both stage surfaces (the wizard and the section's page tabs) show WizardScoringProgress above. */}
        {!stageView && <ListingProgressOverlay onCancel={cancelSearch} />}
        {/* EQUAL HEIGHTS ACROSS A ROW. The machine beds inside a card are drawn to scale, so a plan with a taller
            die-cutting bed makes a taller card -- and the Select buttons ended up at three different heights,
            which reads as three unrelated blocks rather than one row of choices. alignItems stretch gives every
            card the row's height and the button (marginTop:auto) drops to the bottom of it.

            CENTRED WHILE THE ROW IS NOT FULL. One or two plans left-aligned sit in a corner with a wide empty
            space beside them, reading as "something failed to load". Only up to the row's capacity: past that
            the cards fill the row anyway, and centring would leave a stray last row floating mid-width. */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: sortedLists.length < cardsPerRow ? 'center' : 'flex-start',
          alignItems: 'stretch'
        }}>
          {sortedLists.map((list) => renderManufacturingLineList(list))}
        </div>
      </div>
    );
  };

  // THE PICKED VIEW: the plan on its own, centred, and its solutions under it -- the same shape the legacy page
  // uses (LayoutNavSectionCombinationList: Go Back, the selected card centred, an <hr />, then the solutions).
  //
  // It REPLACES the listing rather than appending to it. Appending is what the first cut did, and with 237 plan
  // cards above them the solutions landed several screens down: clicking a card looked like nothing happened,
  // even though the solve had run and the cards had rendered. A DOM assertion could not see that -- only opening
  // the page could.
  const renderPickedPlan = () => {
    const picked = manufacturingLineLists.find((list) => list.identifier === pickedList);

    return (
      <>
        {renderGoBack()}
        {picked ? (
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderManufacturingLineList(picked, { alone: true })}
          </div>
        ) : null}
        <hr />
        {renderSolutions()}
      </>
    );
  };

  // THE CHOSEN SOLUTION, and the section priced from it. It REPLACES the listing and the solutions, the way the
  // legacy page does: once a solution is chosen the question is no longer which one, and leaving the whole
  // listing above the prices buries them (the same reason the picked plan replaces the listing).
  // THE PAGE'S PRICING TAB. Once a solution is chosen it is the priced view (renderSelectedSolution). Arriving
  // here with a solution only MARKED (from the Solution tab) is what commits it -- the pricing commit effect
  // turns the mark into a selection, which posts select-solution and prices the section -- so this shows a short
  // "pricing" note while that is in flight. With nothing marked or chosen it points the user back to the
  // Solution tab.
  const renderPricing = () => {
    if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }
    const message = hasSelection(markedSolution)
      ? t('quote_builder.frank_pricing_in_progress')
      : t('quote_builder.frank_pricing_needs_solution');
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-soft, #4e5865)', fontSize: '14px', padding: '48px 20px' }}>
        {message}
      </div>
    );
  };

  // withoutPrices - show WHAT was chosen without the cost sections. Used by a linked section's Manufacturing
  //                 Line and Solution tabs: those tabs answer "what is this made on", and its prices live on
  //                 the Pricing tab, exactly as they do for a section that chose for itself.
  // showOnly       - 'plan' or 'solution', to draw just that card. A linked section's Manufacturing Line tab
  //                 must show the manufacturing LINE, and its Solution tab the SOLUTION -- the same question
  //                 each tab answers everywhere else. Absent draws both, which is what the stacked view and the
  //                 Pricing tab want (the Pricing tab keeps the solution card mounted-but-hidden, because it
  //                 writes the num_total_* totals the cost forms read).
  const renderSelectedSolution = ({ withoutPrices = false, showOnly = null } = {}) => {
    // The plan the section is priced from -- the very card the viewer picked. Taken from
    // effectiveManufacturingLineLists so it is found both in-session (from the listing) and after a refresh (from
    // pricedPlanData, fetched unfiltered because the default-filtered listing no longer holds it). Still absent
    // for a selection made on the legacy page, which stamps no manufacturing line identifier on its cards; the
    // prices are then about the chosen solution regardless, so the plan card is simply left out.
    const pricedPlan = planOnScreen
      ? effectiveManufacturingLineLists.find((list) => list.identifier === planOnScreen)
      : null;

    // On the page's PRICING tab the plan and the chosen solution have their own tabs (Manufacturing Line /
    // Solution), so this view shows ONLY the prices: the "choose a different solution" button and the plan +
    // solution cards below are hidden (the solution card stays mounted but display:none, because it writes the
    // num_total_* totals the cost forms read -- removing it would leave those columns at 0). The legacy stacked
    // view (no stageView) keeps them: it is the whole flow in one column.
    const pricingTab = stageView === 'pricing';

    return (
      <>
        {/* Not on a LINKED section: its solution is not its own to swap, and de-selecting here would clear the
            section that leads it. The note above says where to change it. */}
        {!pricingTab && !isLinked && (
        <button
          type="button"
          data-frank-choose-another-solution
          // Through the SHARED reset handler, so the server forgets the selection too. Clearing only the page's
          // state left user_quote_nav_sections.selected_lists and the quote's builder_payload still holding the
          // rejected solution: a refresh reopened the section priced from it, and the Manage tab's totals went on
          // counting it. It also resets the linked sections and reloads when the server says to, which the legacy
          // section's own Back button has always done -- this page had a second button that did none of it.
          onClick={() => resetSelectedListHandler({
            index: navSectionIndex,
            setSelectedList: setSelectedSolutions
          })}
          style={{
            marginBottom: '12px', padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
            border: '1px solid var(--rule, #e3e7ec)', borderRadius: 'var(--dct-radius, 8px)',
            background: 'var(--surface, #ffffff)', color: 'var(--ink-soft, #4e5865)'
          }}
        >
          ← {t('quote_builder.choose_a_different_solution')}
        </button>
        )}

        {/* WHAT THE PRICES ARE ABOUT, kept above them: the plan the section is manufactured on, and the solution
            card that was chosen off it -- the two cards the viewer clicked through to get here. Without them the
            priced view is a page of forms with no subject, and the only way to see which plan and which solution
            it was pricing was to go back, which throws the selection away.

            Both are the cards the rest of the page already draws, not a summary written again here: the plan
            through renderManufacturingLineList (the listing's own card), the solution through the legacy
            LayoutNavSectionCombinationListList (the same card the solutions grid shows). So they read identically
            to the ones that were clicked.

            READ-ONLY, both of them: choosing again is the button above, which de-selects on the server too. */}
        <div
          data-frank-priced-from
          style={{
            // Hidden on the Pricing tab (its plan + solution live on the other two tabs) but kept mounted, so
            // the solution card below still writes the num_total_* totals the cost forms read.
            display: pricingTab ? 'none' : 'flex', flexWrap: 'wrap', justifyContent: 'center',
            alignItems: 'flex-start', marginBottom: '12px'
          }}
        >
          {showOnly !== 'solution' && pricedPlan
            ? renderManufacturingLineList(pricedPlan, { alone: true, readOnly: true })
            : null}
          {showOnly === 'plan' ? null : (
          <LayoutNavSectionCombinationListList
            // THE SELECTION ITSELF, deliberately not a copy. The shared card writes num_total_diecutting /
            // num_total_printing / num_of_drawings_in_set back into whatever `list` it is handed once its score
            // lands, and the selection is what carries those onward -- a copy would leave the page holding a
            // solution the card had already corrected. (The copy count they feed is no longer DRAWN under the
            // pictures: FrankSolutionDimensions dropped it, since the copies are visible in the picture itself.)
            //
            // What made that mutation dangerous was never the mutation -- it was that a stale selection could
            // be visible under the WRONG section index for one render, which turned the drift into a post
            // against another section. The selection is tagged with its section now, so it cannot be.
            list={selectedSolutions}
            count={selectedSolutions[0].count}
            // A no-op: the card is here to say WHICH solution is priced, not to choose one. The shared card
            // always wires its container to setSelectedList, so this is where the click is dropped.
            setSelectedList={() => {}}
          />
          )}
        </div>

        {withoutPrices ? null : (
          <SelectedSolutionSections
            config={config}
            navSectionIndex={navSectionIndex}
            selectedSolutions={selectedSolutions}
            setSelectedSolutions={setSelectedSolutions}
            hideBackButton={pricingTab}
          />
        )}
      </>
    );
  };

  return (
    <ProgressProvider queue={sectionAnswerQueue}>
      <div ref={sectionRootRef} style={{ marginTop: '10px', marginBottom: '10px', position: 'relative' }}>

      {/* Asked ONCE for the whole section, above the sub_sections they all narrow. Hidden once a plan is picked
          or a solution chosen: the filters narrow the LISTING, and the listing is not what is on screen then.
          While a change re-lists, the whole block is dimmed and its clicks blocked (pointerEvents), on top of
          each input being disabled -- so no filter can be touched until the results are back. */}
      {/* The machine filters are not drawn for a linked section: they narrow a listing it does not have, and
          offering them is what made its old empty state read as "you have filtered everything out". Why there
          is nothing to choose is said by the marker on the Manufacturing Line tab. */}
      {!isLinked && (stageView === 'line' || (!stageView && !pickedList && !hasSelection(selectedSolutions))) ? (
        <div
          data-section-machine-filters
          aria-busy={filtersLocked}
          style={{
            marginBottom: '12px', opacity: filtersLocked ? 0.55 : 1,
            pointerEvents: filtersLocked ? 'none' : 'auto', transition: 'opacity 120ms ease'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            {renderSectionFilters()}
          </div>
          {adminDebugVisible && <DebugPanel title="Filters">{renderFilterToggles()}</DebugPanel>}
          {adminDebugVisible && <DebugPanel title="Area ratio">{renderAreaRatioControl()}</DebugPanel>}
        </div>
      ) : null}

      {/* THE WAY OUT WHILE THE LISTING HALF IS IN FLIGHT. Both progress surfaces carry a Cancel, but neither is
          on screen until the SCORING starts -- so a find that takes its time (a cold section, a slow server)
          left the filters locked with nothing to press, which is the trap the lock was designed not to be.
          Shown only in that gap: once the bar appears, its own Cancel takes over and there is never a second
          one. Outside the block above, whose pointer events are off while it is locked. */}
      {filtersLocked && !scoringInProgress && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '12px' }}>
          <button
            type="button"
            data-cancel-search
            onClick={cancelSearch}
            className="dct-button dct-button--secondary dct-button--small"
          >
            {t('quote_builder.cancel')}
          </button>
        </div>
      )}

      {(() => {
        // A LINKED SECTION NEVER LISTS AND NEVER SOLVES -- those two are the section that leads it. Everything
        // else is unchanged: its Pricing tab is its own, and its priced view (the inherited plan, the inherited
        // solution, and THIS section's costs under them) renders exactly as any other section's.
        if (isLinked) {
          if (!hasSelection(selectedSolutions)) { return renderInheritedPending(); }
          // EACH TAB ANSWERS ITS OWN QUESTION, as it does for every other section: the Manufacturing Line tab
          // shows the manufacturing LINE it inherited, the Solution tab the SOLUTION, and Pricing the costs.
          // Showing the solution card under "Manufacturing Line" answers the wrong question with the wrong
          // card; showing the cost sections under all three leaves the tabs meaning nothing here.
          if (stageView === 'pricing') { return renderPricing(); }
          if (stageView === 'line') { return renderSelectedSolution({ withoutPrices: true, showOnly: 'plan' }); }
          if (stageView === 'solution') {
            return renderSelectedSolution({ withoutPrices: true, showOnly: 'solution' });
          }
          // The stacked view (no tabs) is the whole flow in one column, for a linked section as for any other.
          return renderSelectedSolution();
        }

        // A stage view (wizard stage or a page section tab) renders ONE sub-view; the wizard / the page tabs
        // drive the progression between them.
        if (stageView === 'line') { return renderListing(); }
        if (stageView === 'solution') { return renderSolutions(); }
        if (stageView === 'pricing') { return renderPricing(); }

        // The legacy stacked view: the whole flow in one column.
        if (hasSelection(selectedSolutions)) { return renderSelectedSolution(); }

        return pickedList ? renderPickedPlan() : renderListing();
      })()}

      {/* The sub_sections themselves are NOT listed: each is the working behind the lists, not an answer --
          "Kapak" on its own makes no shirt box, it makes a cover. Every one of its manufacturing lines already
          appears above, inside the lists that use it. */}
      </div>
    </ProgressProvider>
  );
};

export default ManufacturingLineLists;
